import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import {
  Select,
  Row,
  Col,
  Tag,
  message,
  Flex,
  Spin,
  Form,
  Table,
  InputNumber,
  Empty,
  Upload,
  Input,
  InputRef,
  Timeline,
  Popover,
  Badge,
  Slider,
  Drawer,
} from "antd";
import {
  FC,
  forwardRef,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  DecimalInput,
  decimalFieldRules,
  formatCurrency,
  isValidDecimal,
  resourceLineTotal,
  toolLineTotal,
} from "./timelogAmountUtils";
import {
  FormProvider,
  useForm,
  useFormContext,
  Controller,
} from "react-hook-form";
import dayjs from "dayjs";
import {
  Alert,
  Button,
  Card,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import FormInput from "../../form-components/InputComponent";
import FormSelect, {
  SelectOption,
} from "../../form-components/SelectComponent";
import FormDatePicker from "../../form-components/DatePicker";
import { projectsApis } from "../../../api/projects";
import { useSelector, useDispatch } from "react-redux";
import { Icon } from "../../../components/Component";
import Swal from "sweetalert2";
import {
  LoadingOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { RootState } from "../../../store/store";
import { tasksApis } from "../../../api/NewTasks";
import { logHoursApis } from "../../../api/TimeLog";
import { useLocation } from "react-router";
import { issueApis } from "../../../api/Issue";
import {
  ViewAccess,
  EditAccess,
  CancelAccess,
} from "../../../components/accessControl/AccessControl";
import { updateTimeLogInDetailModalSlice } from "../../../store/slices/projectManagement/time-log-view/TimeLogDetailView";
import TimeLogToolMaterialsEditor, {
  type TimeLogMaterialRow,
} from "./TimeLogToolMaterialsEditor";

const hasActual = (r: TimeLogMaterialRow) =>
  (r.actualQuantity != null && `${r.actualQuantity}` !== "") ||
  (r.actualRate != null && `${r.actualRate}` !== "");
const toActualNum = (v: any) =>
  v == null || `${v}` === "" ? undefined : Number(v);

// Diff a tool's current actual-material rows against the originally-loaded
// time-log materials. Materials are referenced by taskToolMaterialId (planned),
// while existing actuals carry timeLogToolMaterialId.
//  - new: a planned row with actuals entered and no existing time-log row
//  - updated: an existing time-log row whose actuals changed
//  - deleted: an existing time-log row whose actuals were cleared
const buildTimelogMaterialsDiff = (
  rows: TimeLogMaterialRow[] = [],
  existing: any[] = [],
) => {
  const newLines = rows
    .filter((r) => r.timeLogToolMaterialId == null && hasActual(r))
    .map((r) => ({
      taskToolMaterialId: r.taskToolMaterialId,
      actualQuantity: toActualNum(r.actualQuantity),
      actualRate: toActualNum(r.actualRate),
    }));

  const updatedLines = rows
    .filter((r) => r.timeLogToolMaterialId != null && hasActual(r))
    .filter((r) => {
      const prev = (existing || []).find(
        (e: any) => e.timeLogToolMaterialId === r.timeLogToolMaterialId,
      );
      if (!prev) return false;
      return (
        Number(prev.actualQuantity ?? "") !==
          Number(r.actualQuantity ?? "") ||
        Number(prev.actualRate ?? "") !== Number(r.actualRate ?? "")
      );
    })
    .map((r) => ({
      timeLogToolMaterialId: r.timeLogToolMaterialId as number,
      actualQuantity: toActualNum(r.actualQuantity),
      actualRate: toActualNum(r.actualRate),
    }));

  // Existing time-log materials whose row no longer has actuals → delete.
  const keptIds = new Set(
    rows
      .filter((r) => r.timeLogToolMaterialId != null && hasActual(r))
      .map((r) => r.timeLogToolMaterialId),
  );
  const deletedLines = (existing || [])
    .filter((m: any) => !keptIds.has(m.timeLogToolMaterialId))
    .map((m: any) => ({ timeLogToolMaterialId: m.timeLogToolMaterialId }));

  return { newLines, updatedLines, deletedLines };
};

const timelogMaterialsDiffHasChanges = (diff: {
  newLines: any[];
  updatedLines: any[];
  deletedLines: any[];
}) =>
  diff.newLines.length > 0 ||
  diff.updatedLines.length > 0 ||
  diff.deletedLines.length > 0;

export interface TimeLogUpdatePayload {
  projectId: number;
  activityId?: number;
  taskId?: number;
  timeLogId?: number;
  issueId?: number;
  logTitle: string;
  // logDate: string;
  logDate: dayjs.Dayjs | undefined;
  loginTime: string;
  logoutTime: string;
  billingType: string;
  notes: string;
  shiftname: string;
  taskCompletionPercentage?: number | null;
  timeLogResources?: {
    newLines: Array<{
      taskResourceId: number;
      actualResourceCount: number;
      actualQuantity: number;
      actualRate: number;
    }>;
    updatedLines: Array<{
      timeLogResourceId: number;
      actualResourceCount: number;
      actualQuantity: number;
      actualRate: number;
    }>;
    deletedLines: Array<{
      timeLogResourceId: number;
    }>;
  } | null;
  timeLogTools?: {
    newLines: Array<{
      taskToolId: number;
      actualQuantity: number;
      actualRate: number;
      toolMaterials?: Array<any>;
    }>;
    updatedLines: Array<{
      timeLogToolId: number;
      actualQuantity: number;
      actualRate: number;
      toolMaterials?: {
        newLines?: Array<any>;
        updatedLines?: Array<any>;
        deletedLines?: Array<any>;
      };
    }>;
    deletedLines: Array<{
      timeLogToolId: number;
    }>;
  } | null;
}

export interface ExtendedSelectOption extends SelectOption {
  type: "task" | "issue";
}

interface ActivityResource {
  activityResourceId: number;
  taskResourceId?: number;
  laborTypeId: number;
  laborTypeName: string;
  laborCategoryId: number;
  laborCategory: string;
  status: string;
  resourceCount?: number;
}

interface ResourceEditableCellProps extends React.HTMLAttributes<HTMLElement> {
  dataIndex: string;
  title: string;
  record: any;
  children: React.ReactNode;
  form: any;
  editing: boolean;
}

const ResourceEditableCell: React.FC<ResourceEditableCellProps> = ({
  dataIndex,
  title,
  record,
  children,
  form,
  editing,
  ...restProps
}) => {
  if (dataIndex === "actualResourceCount") {
    const fieldName = `actualResourceCount_${record?.activityResourceId}`;

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={fieldName}
            style={{ margin: 0 }}
            initialValue={record?.actualResourceCount || undefined}
            rules={[
              {
                required: true,
                type: "number",
                min: 1,
                message: `${title} must be at least 1`,
              },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={1}
              placeholder="Enter count"
            />
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  }
  if (dataIndex === "actualQuantity" || dataIndex === "actualRate") {
    const fieldName = `${dataIndex}_${record?.activityResourceId}`;
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={fieldName}
            style={{ margin: 0 }}
            initialValue={record?.[dataIndex]}
            rules={decimalFieldRules(title)}
          >
            <DecimalInput placeholder={`Enter ${title.toLowerCase()}`} />
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  }
  return <td {...restProps}>{children}</td>;
};

interface ToolEditableCellProps extends React.HTMLAttributes<HTMLElement> {
  dataIndex: string;
  title: string;
  record: any;
  children: React.ReactNode;
  form: any;
  editing: boolean;
}

const ToolEditableCell: React.FC<ToolEditableCellProps> = ({
  dataIndex,
  title,
  record,
  children,
  form,
  editing,
  ...restProps
}) => {
  if (dataIndex === "actualQuantity" || dataIndex === "actualRate") {
    const fieldName = `${dataIndex}_${record?.taskToolId}`;
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={fieldName}
            style={{ margin: 0 }}
            initialValue={record?.[dataIndex]}
            rules={decimalFieldRules(title)}
          >
            <DecimalInput placeholder={`Enter ${title.toLowerCase()}`} />
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  }
  return <td {...restProps}>{children}</td>;
};

type TimeLogFormFields = {
  linkedTask: string;
  linkedEntity: string | number | null;
  // logDate: string;
  logDate: dayjs.Dayjs | undefined;
  user: string;
  billingType: string;
  notes: string;
  shiftname: string;
  taskCompletionPercentage?: number | null;
  taskName: string;
  workCategoryId: string;
  reminderFlag: boolean;
  taskDescription: string;
  taskOwnerId: string;
  taskPlannedStartDate: any;
  taskPlannedEndDate: any;
  priorityId: string;
  isBillable: any;
  activityResourceIds: string[];
  taskBoqs: string[];
  projectUserId: string[];
  boqLineIds: string[];
};

export interface TimeLogInformationByIdRef {
  resetEditMode: () => void;
}

const InlineFieldEdit: FC<{
  label: string;
  name: string;
  children: React.ReactNode;
  isEditMode: boolean;
  isRequiredField?: boolean;
}> = ({ label, name, children, isEditMode, isRequiredField = false }) => {
  const {
    getFieldState,
    formState: { errors },
  } = useFormContext();

  const isRequired =
    isEditMode && (!!getFieldState(name)?.isTouched || !!errors[name]);

  const shouldShowStar = isEditMode && isRequiredField;

  return (
    <div
      className="d-flex align-items-center"
      style={{ gap: "12px", minHeight: "40px", fontSize: "13px" }}
    >
      <span className="fw-semibold text-dark" style={{ minWidth: 140 }}>
        {label}
        {shouldShowStar && <span className="text-danger ms-1">*</span>}
      </span>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
};

const TimeLogInformationById = forwardRef<
  TimeLogInformationByIdRef,
  {
    refreshData: any;
    isFromWorkUI?: boolean;
    refreshTimeLogList?: () => void;
    onTaskCompletionUpdate?: () => void;
    initialEditMode?: boolean;
    onBackToSummary?: () => void;
  }
>(
  (
    {
      refreshData,
      isFromWorkUI = false,
      refreshTimeLogList,
      onTaskCompletionUpdate,
      initialEditMode = false,
      onBackToSummary,
    },
    ref
  ) => {
    // Get time log data from Redux state to ensure we always have the latest data
    const { data: timeLogModalData } = useSelector(
      (state: RootState) => state.TimeLogListDetailModal
    );
    const reduxTimeLogData = timeLogModalData.selectedTimeLogDetails;

    // Use Redux data if available, otherwise fall back to prop data
    const timeLogData =
      reduxTimeLogData?.rawData ||
      reduxTimeLogData ||
      refreshData?.rawData ||
      refreshData;

    const files = timeLogData?.dms || [];
    const [uploadedFiles, setUploadedFiles] = useState<any[]>(
      timeLogData?.dms || []
    );
    const [fileRemarks, setFileRemarks] = useState<{ [key: number]: string }>(
      {}
    );
    const [newFileData, setNewFileData] = useState<
      Array<{
        indexOfFileLocated: number;
        linkType: string;
        linkTo: string;
        remarks: string;
        action: string;
        timeLogId?: number;
        docName: string;
        docId?: number;
      }>
    >([]);
    const [deletedFileData, setDeletedFileData] = useState<
      Array<{ docId: number }>
    >([]);
    // console.log("Refresh Data:", timeLogData); //

    // Add this useEffect to handle file remarks initialization
    useEffect(() => {
      if (timeLogData?.dms && timeLogData.dms.length > 0) {
        setUploadedFiles(timeLogData.dms);

        const initialRemarks: { [key: number]: string } = {};
        timeLogData.dms.forEach((file: any, idx: number) => {
          initialRemarks[idx] = file.remarks || "Related to timelog";
        });
        setFileRemarks(initialRemarks);
      } else {
        setUploadedFiles([]); // Clear files if none in new log
        setFileRemarks({});
      }
      setNewFileData([]);
      setDeletedFileData([]);
    }, [timeLogData]);

    // Expose the resetEditMode method to parent via ref
    useImperativeHandle(ref, () => ({
      resetEditMode: () => {
        setIsEditMode(false);
        reset(); // Also reset the form
      },
    }));

    const dispatch = useDispatch();

    const { data: timeLogModalData2 } = useSelector(
      (state: RootState) => state.TimeLogListDetailModal
    );

    const [screenSize, setScreenSize] = useState(0);
    const viewChange = () => setScreenSize(window.innerWidth);
    const [isEmpty, setIsEmpty] = useState(false);

    const { data: currentUser } = useSelector(
      (state: any) => state.currentUser
    );

    // Check if current user can edit time logs
    const canEdit = () => {
      if (!currentUser) return false;

      // Admin can edit any time log
      if (currentUser.genericRole?.toLowerCase() === "admin") return true;

      // Task owner can edit time logs for their tasks
      // Check if the time log is associated with a task and if current user is the task owner
      if (timeLogData?.taskId && timeLogData?.taskOwnerId) {
        if (String(currentUser.id) === String(timeLogData.taskOwnerId))
          return true;
      }

      // Issue owner can edit time logs for their issues
      // Check if the time log is associated with an issue and if current user is the issue owner
      if (timeLogData?.issueId) {
        // Check various possible field names for issue owner
        const issueOwnerId =
          timeLogData?.issueOwnerId ||
          timeLogData?.issueAssignedToId ||
          timeLogData?.assignedTo;
        if (issueOwnerId && String(currentUser.id) === String(issueOwnerId)) {
          return true;
        }
      }

      // Original logic: Creator can edit their own time logs
      if (String(currentUser?.id) === String(timeLogData?.createdBy))
        return true;

      return false;
    };

    const canEditTimeLog = canEdit();

    const location = useLocation();
    const { id: routeProjectId, projectName: routeProjectName } =
      location.state || {};
    // Prioritize propProjectId, then routeProjectId
    const currentProjectId =
      timeLogModalData2?.selectedTimeLogDetails?.projectId || routeProjectId;

    const [messageApi, contextHolder] = message.useMessage();
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(initialEditMode);
    const [projectUsers, setProjectUsers] = useState<SelectOption[]>([]);
    const [disableSubmit, setDisableSubmit] = useState(false);

    // Resource allocation state
    const [activityResourcesList, setActivityResourcesList] = useState<
      ActivityResource[]
    >([]);
    const [selectedResourceKeys, setSelectedResourceKeys] = useState<
      React.Key[]
    >([]);
    const [editingResourceKeys, setEditingResourceKeys] = useState<string[]>(
      []
    );
    const [resourceForm] = Form.useForm();
    const [selectedActivityId, setSelectedActivityId] = useState<number | null>(
      null
    );

    // Map of workUomId -> uomName, used to label planned quantities.
    const [workUomMap, setWorkUomMap] = useState<Record<number, string>>({});

    // Tools & Machinery state
    const [activityToolsList, setActivityToolsList] = useState<any[]>([]);
    const [selectedToolKeys, setSelectedToolKeys] = useState<React.Key[]>([]);
    const [editingToolKeys, setEditingToolKeys] = useState<string[]>([]);
    const [toolForm] = Form.useForm();

    // Per-tool actual material usage (keyed by taskToolId). Pre-populated from
    // the task tool's planned materials merged with this time log's saved
    // actuals; only Actual Qty/Rate are editable.
    const [materialRowsByTool, setMaterialRowsByTool] = useState<
      Record<number, TimeLogMaterialRow[]>
    >({});
    const [materialsDrawerToolId, setMaterialsDrawerToolId] = useState<
      number | null
    >(null);
    const [materialNameMap, setMaterialNameMap] = useState<
      Record<number, string>
    >({});

    useEffect(() => {
      (async () => {
        try {
          const matRes = await tasksApis.fetchMachineryMaterialDropDown();
          if (matRes?.status) {
            const map: Record<number, string> = {};
            (matRes.data || []).forEach((m: any) => {
              map[m.id ?? m.materialId] = m.materialName ?? m.name ?? m.label;
            });
            setMaterialNameMap(map);
          }
        } catch {
          /* ignore */
        }
      })();
    }, []);

    const [form] = Form.useForm();
    const [shiftOptions, setShiftOptions] = useState<SelectOption[]>([]);

    const [activeTab, setActiveTab] = useState("stream");

    const remarksRefs = useRef<(InputRef | null)[]>([]);

    useEffect(() => {
      remarksRefs.current = remarksRefs.current.slice(0, uploadedFiles.length);
    }, [uploadedFiles.length]);

    const [taskDropdownData, setTaskDropdownData] = useState<SelectOption[]>(
      []
    );
    const [issueDropdownData, setIssueDropdownData] = useState<SelectOption[]>(
      []
    );
    const [combinedOptions, setCombinedOptions] = useState<
      { label: string; value: string | number; type: "task" | "issue" }[]
    >([]);

    // console.log(combinedOptions,'combinedOptions');
    const fetchedProjects = useRef<Set<number>>(new Set()); // Track fetched projectIds

    // timepopover
    const [startHour, setStartHour] = useState("09");
    const [startMinute, setStartMinute] = useState("00");
    const [startMeridiem, setStartMeridiem] = useState("am");

    const [endHour, setEndHour] = useState("05");
    const [endMinute, setEndMinute] = useState("00");
    const [endMeridiem, setEndMeridiem] = useState("pm");

    const [dailyLogHours, setDailyLogHours] = useState("00:00");
    const [selectedStartTime, setSelectedStartTime] = useState("");
    const [selectedEndTime, setSelectedEndTime] = useState("");

    const hours = Array.from({ length: 12 }, (_, i) =>
      String(i + 1).padStart(2, "0")
    );
    const minutes = ["00", "15", "30", "45"];
    const meridiems = ["am", "pm"];

    useEffect(() => {
      const start = `${startHour}:${startMinute} ${startMeridiem}`;
      const end = `${endHour}:${endMinute} ${endMeridiem}`;
      setSelectedStartTime(start);
      setSelectedEndTime(end);
      setDailyLogHours(calculateHours(start, end));
    }, [
      startHour,
      startMinute,
      startMeridiem,
      endHour,
      endMinute,
      endMeridiem,
    ]);

    const calculateHours = (start: string, end: string) => {
      const to24hr = (t: string) => {
        const [h, m] = t.split(":");
        const [min, mer] = m.split(" ");
        let hour = parseInt(h);
        if (mer === "pm" && hour !== 12) hour += 12;
        if (mer === "am" && hour === 12) hour = 0;
        return dayjs().hour(hour).minute(parseInt(min));
      };
      const diff = to24hr(end).diff(to24hr(start), "minute");
      const hrs = Math.floor(diff / 60);
      const mins = diff % 60;
      return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
    };

    // Format time from "HH:mm:ss" to "hh:mm A" for display
    const formatTimeForDisplay = (timeString: string) => {
      if (!timeString) return "";
      return dayjs(`2000-01-01 ${timeString}`).format("hh:mm A");
    };

    const parseApiDate = (
      dateString: string | undefined
    ): dayjs.Dayjs | undefined => {
      return dateString ? dayjs(dateString, "YYYY-MM-DD") : undefined;
    };

    const fetchShiftDropdownData = async (projectId: number) => {
      try {
        const response = await logHoursApis.getShifts(projectId); // call your shift API
        const { data, status } = response;
        if (status) {
          const options = data?.map((shift: any) => ({
            label: shift.shiftName,
            value: shift.shiftId,
          }));
          setShiftOptions(options);
        } else {
          setShiftOptions([]);
        }
      } catch (error) {
        console.error("Failed to fetch shifts:", error);
        setShiftOptions([]);
      }
    };

    const fetchTaskDropDownData = async (projectId: number) => {
      if (fetchedProjects.current.has(projectId)) return; // Skip if already fetched
      try {
        const apiResponse = await tasksApis.fetchTaskDropDown({ projectId });
        const { data, status } = apiResponse;
        if (status) {
          let options = data?.map((item: any) => {
            return {
              label: item.taskName,
              value: item.taskId,
              overallTaskCompletionPercentage: Number(
                item.overallTaskCompletionPercentage ?? 0
              ),
            };
          });
          setTaskDropdownData(options);
        } else {
          setTaskDropdownData([]);
        }
      } catch (error) {
        console.error("Failed to fetch tasks:", error);
        setTaskDropdownData([]);
      } finally {
        fetchedProjects.current.add(projectId); // Mark as fetched
      }
    };

    // const TabsList = [
    //   {
    //     tabKey: "stream",
    //     label: "Activity Stream",
    //   },
    // ];

    const methods = useForm<TimeLogFormFields>({ mode: "onTouched" });
    const { control, handleSubmit, setValue, watch, reset } = methods;

    // Reset edit mode when the issue data changes — honor the initialEditMode
    // prop so that opening the form from a parent's "Edit" action keeps the
    // form in edit mode instead of falling back to view mode.
    useEffect(() => {
      setIsEditMode(initialEditMode);
      reset();
    }, [refreshData, reset, initialEditMode]);

    const convertSecondsToTime = (seconds: number) => {
      const hrs = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      return `${hrs}:${mins.toString().padStart(2, "0")}`;
    };

    useEffect(() => {
      // console.log("currentProjectId:", currentProjectId); // Debug log
      if (currentProjectId && !fetchedProjects.current.has(currentProjectId)) {
        fetchProjectUsers(currentProjectId);
        fetchTaskDropDownData(currentProjectId);
        fetchIssueDropDownData(currentProjectId);
        fetchShiftDropdownData(currentProjectId);
      }
    }, [currentProjectId]);

    useEffect(() => {
      viewChange();
      if (timeLogData) {
        const initialLinkedEntity = timeLogData.taskId
          ? { value: timeLogData.taskId, type: "task" }
          : timeLogData.issueId
          ? { value: timeLogData.issueId, type: "issue" }
          : null;
        reset({
          linkedEntity: initialLinkedEntity?.value || null,
          user: timeLogData?.userId ?? null,
          logDate: parseApiDate(timeLogData?.logDate),
          billingType: timeLogData?.billingType?.toLowerCase() || "",
          notes: timeLogData?.notes || "",
          shiftname: timeLogData?.shiftName || "",
          taskCompletionPercentage:
            timeLogData?.taskCompletionPercentage ?? null,
        });

        // Set time pickers from the API data
        if (timeLogData.loginTime && timeLogData.logoutTime) {
          const loginDisplayTime = formatTimeForDisplay(timeLogData.loginTime);
          const logoutDisplayTime = formatTimeForDisplay(
            timeLogData.logoutTime
          );

          // Parse the display times to set the time picker values
          const parseDisplayTime = (displayTime: string) => {
            const [time, meridiem] = displayTime.split(" ");
            const [hour, minute] = time.split(":");
            return {
              hour,
              minute,
              meridiem: meridiem.toLowerCase(),
            };
          };

          const start = parseDisplayTime(loginDisplayTime);
          const end = parseDisplayTime(logoutDisplayTime);

          setStartHour(start.hour);
          setStartMinute(start.minute);
          setStartMeridiem(start.meridiem);
          setEndHour(end.hour);
          setEndMinute(end.minute);
          setEndMeridiem(end.meridiem);

          setSelectedStartTime(loginDisplayTime);
          setSelectedEndTime(logoutDisplayTime);
          setDailyLogHours(
            convertSecondsToTime(timeLogData.totalWorkHoursInSec)
          );
        }
      }
    }, [timeLogData, currentProjectId, reset]);

    const fetchProjectUsers = async (projectId: number) => {
      if (fetchedProjects.current.has(projectId)) return; // Skip if already fetched
      try {
        const response: any = await projectsApis.fetchProjectUsers(projectId);
        const { data: usersList, status } = response || {};
        if (status) {
          const formatted = usersList?.map(
            (user: { userId: string; userName: string; roleName: string }) => ({
              label: user?.userName,
              value: +user?.userId,
              roleName: user?.roleName,
            })
          );
          setProjectUsers(formatted);
        }
      } catch (error) {
        console.log(error);
      } finally {
        fetchedProjects.current.add(projectId); // Mark as fetched
      }
    };

    const fetchIssueDropDownData = async (projectId: number) => {
      if (fetchedProjects.current.has(projectId)) return; // Skip if already fetched
      try {
        const apiResponse = await issueApis.fetchIssueDropDown(projectId);
        const { data, status } = apiResponse;
        if (status) {
          let options = data?.map((item: any) => {
            return {
              label: item.issueName,
              value: item.issueId,
              type: "issue",
            };
          });
          setIssueDropdownData(options);
        } else {
          setIssueDropdownData([]);
        }
      } catch (error) {
        console.error("Failed to fetch tasks:", error);
        setIssueDropdownData([]);
      } finally {
        fetchedProjects.current.add(projectId); // Mark as fetched
      }
    };

    useEffect(() => {
      const combinedOptions = [
        ...taskDropdownData.map((task) => ({
          ...task,
          label: `[Task] ${task.label}`,
        })),
        ...issueDropdownData.map((issue) => ({
          ...issue,
          label: `[Issue] ${issue.label}`,
        })),
      ];
      setCombinedOptions(combinedOptions as ExtendedSelectOption[]); // Cast to ExtendedSelectOption[]
    }, [taskDropdownData, issueDropdownData]);

    // Overall task completion (B) for the linked task — comes from task dropdown API
    const taskOverallCompletion = useMemo(() => {
      if (!timeLogData?.taskId) return null;
      const task: any = taskDropdownData.find(
        (t: any) => Number(t.value) === Number(timeLogData.taskId)
      );
      return task && task.overallTaskCompletionPercentage !== undefined
        ? Number(task.overallTaskCompletionPercentage)
        : null;
    }, [taskDropdownData, timeLogData?.taskId]);

    // Floor for editable completion = B - X (task progress excluding this log's contribution)
    const completionFloor = useMemo(() => {
      if (taskOverallCompletion === null) return 0;
      const originalDelta = Number(timeLogData?.taskCompletionPercentage ?? 0);
      return Math.max(0, taskOverallCompletion - originalDelta);
    }, [taskOverallCompletion, timeLogData?.taskCompletionPercentage]);

    const onResourceSelectChange = (
      newSelectedRowKeys: React.Key[],
      selectedRows?: any
    ) => {
      setSelectedResourceKeys(newSelectedRowKeys);
      if (!newSelectedRowKeys) {
        setEditingResourceKeys([]);
      }
      const editableRowKeys: any = [];
      newSelectedRowKeys?.forEach((key) => {
        const selectedRow = selectedRows.find(
          (row: any) => `${row.activityResourceId}` === key
        );
        editableRowKeys.push(key);
        if (selectedRow?.resourceCount) {
          resourceForm.setFieldsValue({
            [`resourceCount_${key}`]: selectedRow?.resourceCount,
          });
        }
      });
      setEditingResourceKeys(editableRowKeys);
    };

    const isResourceEditing = (record: any) =>
      editingResourceKeys.includes(`${record.activityResourceId}`);

    const onToolSelectChange = (
      newSelectedRowKeys: React.Key[],
      selectedRows?: any
    ) => {
      setSelectedToolKeys(newSelectedRowKeys);
      if (!newSelectedRowKeys) {
        setEditingToolKeys([]);
      }
      const editableRowKeys: any = [];
      newSelectedRowKeys?.forEach((key) => {
        const selectedRow = selectedRows.find(
          (row: any) => `${row.taskToolId}` === key
        );
        editableRowKeys.push(key);
        // Don't pre-fill actualCount - let user enter their own value
      });
      setEditingToolKeys(editableRowKeys);
    };

    const isToolEditing = (record: any) =>
      editingToolKeys.includes(`${record.taskToolId}`);

    // Bumped on any resource/tool form change so line/section totals re-render.
    const [, forceRecalc] = useReducer((x: number) => x + 1, 0);

    // Dedupe a list of timelog lines by a key (mirrors the view-table logic).
    const dedupeBy = (list: any[], keyName: string) =>
      (list || []).reduce((acc: any[], current: any) => {
        if (
          acc.findIndex((item) => item[keyName] === current[keyName]) === -1
        ) {
          acc.push(current);
        }
        return acc;
      }, []);

    // --- Edit-mode section totals (read from the live form values) ---
    const computeResourceSectionTotalEdit = (): number => {
      const v = resourceForm.getFieldsValue();
      return selectedResourceKeys.reduce<number>(
        (sum, key) =>
          sum +
          resourceLineTotal(
            v[`actualResourceCount_${key}`],
            v[`actualQuantity_${key}`],
            v[`actualRate_${key}`]
          ),
        0
      );
    };
    const computeToolSectionTotalEdit = (): number => {
      const v = toolForm.getFieldsValue();
      return selectedToolKeys.reduce<number>(
        (sum, key) =>
          sum +
          toolLineTotal(v[`actualQuantity_${key}`], v[`actualRate_${key}`]),
        0
      );
    };

    // --- View-mode section totals (read from the saved response values) ---
    const computeResourceSectionTotalView = (): number =>
      dedupeBy(
        timeLogData?.timeLogResources || [],
        "taskResourceId"
      ).reduce<number>(
        (sum, r) =>
          sum +
          resourceLineTotal(r.actualResourceCount, r.actualQuantity, r.actualRate),
        0
      );
    const computeToolSectionTotalView = (): number =>
      dedupeBy(timeLogData?.timeLogTools || [], "taskToolId").reduce<number>(
        (sum, t) => sum + toolLineTotal(t.actualQuantity, t.actualRate),
        0
      );

    // Small footer used under each section (and the grand total).
    const SectionTotalRow: FC<{
      label: string;
      amount: number;
      strong?: boolean;
    }> = ({ label, amount, strong }) => (
      <div
        className="d-flex justify-content-end align-items-center mt-2"
        style={{
          gap: 8,
          fontWeight: strong ? 700 : 600,
          color: "#004293",
          ...(strong
            ? { borderTop: "1px solid #e8e8e8", paddingTop: 8 }
            : {}),
        }}
      >
        <span>{label}:</span>
        <span style={{ fontSize: strong ? 16 : 15 }}>
          {formatCurrency(amount)}
        </span>
      </div>
    );

    // Resolve a record's UOM name from its own fields or the UOM master.
    const resolveUomName = (record: any): string =>
      record?.uomName ||
      record?.workUomName ||
      workUomMap[Number(record?.workUomId)] ||
      "";

    // Load resources when component loads or when task changes
    useEffect(() => {
      if (timeLogData?.taskId) {
        // Fetch the work-UOM master so planned quantities can show the unit
        // name even when the task response returns only workUomId.
        const loadWorkUoms = async () => {
          try {
            const response = await tasksApis.workUom({});
            const { data, status } = response || {};
            if (status && Array.isArray(data)) {
              const map: Record<number, string> = {};
              data.forEach((item: any) => {
                if (item?.workUomId != null) {
                  map[Number(item.workUomId)] =
                    item.uomName ?? item.workUomName ?? item.workUomShortCode;
                }
              });
              setWorkUomMap(map);
            }
          } catch (error) {
            console.error("Error fetching work UOMs:", error);
          }
        };

        loadWorkUoms();

        // Fetch task-specific resources
        const loadTaskResources = async () => {
          try {
            const response = await tasksApis.fetchTaskResources(
              timeLogData.taskId
            );
            const { data, status } = response || {};
            if (status && data) {
              const formattedData = data.map((resource: any) => ({
                activityResourceId: resource.activityResourceId,
                taskResourceId: resource.taskResourceId,
                laborTypeId: resource.laborTypeId,
                laborTypeName: resource.laborTypeName,
                laborCategoryId: resource.laborCategoryId,
                laborCategory: resource.laborCategoryName,
                resourceAtSite: resource.resourceCount,
                resourceCount: resource.resourceCount,
                // Planned reference values from the task
                plannedQuantity: resource.quantity,
                plannedRate: resource.rate,
                uomName: resource.workUomName ?? resource.uomName,
                workUomId: resource.workUomId,
              }));
              setActivityResourcesList(formattedData);
            } else {
              setActivityResourcesList([]);
            }
          } catch (error) {
            console.error("Error fetching task resources:", error);
            setActivityResourcesList([]);
          }
        };

        loadTaskResources();

        // Load task tools
        const loadTaskTools = async () => {
          try {
            const response = await tasksApis.fetchTaskTools(timeLogData.taskId);
            const { data, status } = response || {};
            if (status && data) {
              const formattedData = data.map((tool: any) => ({
                taskToolId: tool.taskToolId,
                toolId: tool.toolId,
                toolName: tool.toolName,
                toolTypeName: tool.toolTypeName,
                count: tool.count,
                // Planned reference values from the task
                serialNumber: tool.serialNumber,
                plannedQuantity: tool.quantity,
                plannedRate: tool.rate,
                uomName: tool.workUomName ?? tool.uomName,
                workUomId: tool.workUomId,
                // Planned split-materials (fixed list) for logging actuals.
                toolMaterials: tool.toolMaterials || [],
              }));
              setActivityToolsList(formattedData);
            } else {
              setActivityToolsList([]);
            }
          } catch (error) {
            console.error("Error fetching task tools:", error);
            setActivityToolsList([]);
          }
        };

        loadTaskTools();
      } else {
        // Clear resources and tools if no task
        setActivityResourcesList([]);
        setSelectedResourceKeys([]);
        setEditingResourceKeys([]);
        resourceForm.resetFields();
        setActivityToolsList([]);
        setSelectedToolKeys([]);
        setEditingToolKeys([]);
        toolForm.resetFields();
      }
    }, [timeLogData?.taskId]);

    // Pre-populate resource selection when entering edit mode (similar to TaskInformationById)
    useEffect(() => {
      if (
        isEditMode &&
        timeLogData?.timeLogResources &&
        activityResourcesList.length > 0
      ) {
        // Remove duplicates from timeLogResources based on taskResourceId
        const uniqueTimeLogResources = timeLogData.timeLogResources.reduce(
          (acc: any[], current: any) => {
            const existingIndex = acc.findIndex(
              (item: any) => item.taskResourceId === current.taskResourceId
            );
            if (existingIndex === -1) {
              acc.push(current);
            }
            return acc;
          },
          []
        );

        // Map timeLogResources to activityResourceIds for selection
        const existingResourceIds = uniqueTimeLogResources
          .map((resource: any) => {
            // Find the corresponding activityResourceId from activityResourcesList
            const matchingResource = activityResourcesList.find(
              (activityResource: any) =>
                activityResource.taskResourceId === resource.taskResourceId
            );
            return matchingResource
              ? `${matchingResource.activityResourceId}`
              : null;
          })
          .filter(Boolean);

        // Remove duplicates from existingResourceIds
        const uniqueExistingResourceIds = existingResourceIds.filter(
          (id: any, index: number) => existingResourceIds.indexOf(id) === index
        );

        setSelectedResourceKeys(uniqueExistingResourceIds);
        setEditingResourceKeys(uniqueExistingResourceIds);

        // Pre-populate resource at site values using activityResourceId
        uniqueTimeLogResources.forEach((resource: any) => {
          const matchingResource = activityResourcesList.find(
            (activityResource: any) =>
              activityResource.taskResourceId === resource.taskResourceId
          );
          if (matchingResource) {
            const id = matchingResource.activityResourceId;
            resourceForm.setFieldsValue({
              [`actualResourceCount_${id}`]: resource.actualResourceCount || 1,
              [`actualQuantity_${id}`]:
                resource.actualQuantity !== undefined &&
                resource.actualQuantity !== null &&
                resource.actualQuantity !== ""
                  ? String(resource.actualQuantity)
                  : undefined,
              [`actualRate_${id}`]:
                resource.actualRate !== undefined &&
                resource.actualRate !== null &&
                resource.actualRate !== ""
                  ? String(resource.actualRate)
                  : undefined,
            });
          }
        });
      }
    }, [
      isEditMode,
      timeLogData?.timeLogResources,
      activityResourcesList,
      resourceForm,
    ]);

    // Pre-populate tools selection when entering edit mode
    useEffect(() => {
      if (
        isEditMode &&
        timeLogData?.timeLogTools &&
        activityToolsList.length > 0
      ) {
        // Remove duplicates from timeLogTools based on taskToolId
        const uniqueTimeLogTools = timeLogData.timeLogTools.reduce(
          (acc: any[], current: any) => {
            const existingIndex = acc.findIndex(
              (item: any) => item.taskToolId === current.taskToolId
            );
            if (existingIndex === -1) {
              acc.push(current);
            }
            return acc;
          },
          []
        );

        // Map timeLogTools to taskToolIds for selection
        const existingToolIds = uniqueTimeLogTools
          .map((tool: any) => {
            // Find the corresponding tool from activityToolsList
            const matchingTool = activityToolsList.find(
              (activityTool: any) => activityTool.taskToolId === tool.taskToolId
            );
            return matchingTool ? `${matchingTool.taskToolId}` : null;
          })
          .filter(Boolean);

        // Remove duplicates from existingToolIds
        const uniqueExistingToolIds = existingToolIds.filter(
          (id: any, index: number) => existingToolIds.indexOf(id) === index
        );

        setSelectedToolKeys(uniqueExistingToolIds);
        setEditingToolKeys(uniqueExistingToolIds);

        // Pre-populate tools at site values using taskToolId
        uniqueTimeLogTools.forEach((tool: any) => {
          const matchingTool = activityToolsList.find(
            (activityTool: any) => activityTool.taskToolId === tool.taskToolId
          );
          if (matchingTool) {
            const id = matchingTool.taskToolId;
            toolForm.setFieldsValue({
              [`actualQuantity_${id}`]:
                tool.actualQuantity !== undefined &&
                tool.actualQuantity !== null &&
                tool.actualQuantity !== ""
                  ? String(tool.actualQuantity)
                  : undefined,
              [`actualRate_${id}`]:
                tool.actualRate !== undefined &&
                tool.actualRate !== null &&
                tool.actualRate !== ""
                  ? String(tool.actualRate)
                  : undefined,
            });
          }
        });
      }
    }, [isEditMode, timeLogData?.timeLogTools, activityToolsList, toolForm]);

    // Build the editable material rows for a tool: planned task-tool materials
    // overlaid with this time log's saved actuals (and any in-form edits).
    const getToolMaterialRows = (taskToolId: number): TimeLogMaterialRow[] => {
      if (materialRowsByTool[taskToolId]) return materialRowsByTool[taskToolId];
      const tool = activityToolsList.find((t) => t.taskToolId === taskToolId);
      const existing =
        (timeLogData?.timeLogTools || []).find(
          (tt: any) => tt.taskToolId === taskToolId,
        )?.toolMaterials || [];
      return (tool?.toolMaterials || []).map((pm: any): TimeLogMaterialRow => {
        const ex = existing.find(
          (e: any) => e.taskToolMaterialId === pm.taskToolMaterialId,
        );
        return {
          taskToolMaterialId: pm.taskToolMaterialId,
          materialId: pm.materialId,
          subMaterialId: pm.subMaterialId,
          plannedQuantity: pm.quantity,
          plannedRate: pm.rate,
          workUomName: pm.workUomName,
          timeLogToolMaterialId: ex?.timeLogToolMaterialId,
          actualQuantity:
            ex?.actualQuantity != null ? Number(ex.actualQuantity) : undefined,
          actualRate: ex?.actualRate != null ? Number(ex.actualRate) : undefined,
        };
      });
    };

    const handleFileUpload = (info: any) => {
      if (info.file.status !== "uploading") {
        const fileObj = info.file.originFileObj;
        const newFileIndex = uploadedFiles.length;

        // Add new file to uploadedFiles list (local state)
        setUploadedFiles((prev) => [...prev, fileObj]);

        // Add default remark
        setFileRemarks((prev) => ({
          ...prev,
          [newFileIndex]: "Related to timelog",
        }));

        // Add to newFileData tracking for API
        const selectedEntity = combinedOptions.find(
          (opt) => opt.value === watch("linkedEntity")
        );
        const linkToLabel =
          selectedEntity?.label.replace(/^\[(Task|Issue)\]\s/, "") ||
          "New Timelog";

        setNewFileData((prev) => [
          ...prev,
          {
            indexOfFileLocated: newFileIndex,
            linkType: "timelog",
            linkTo: linkToLabel,
            remarks: "Related to timelog",
            action: "Create",
            docName: fileObj.name,
          },
        ]);
      }
    };

    // Handle removing files in edit mode
    const handleRemoveFile = (fileToRemove: any) => {
      const index = uploadedFiles.indexOf(fileToRemove);
      if (index === -1) return;

      // Remove file from uploadedFiles state
      setUploadedFiles((prev) => prev.filter((f) => f !== fileToRemove));

      // Remove remarks entry for file
      setFileRemarks((prev) => {
        const newRemarks = { ...prev };
        delete newRemarks[index];
        return newRemarks;
      });

      // Check if file is an existing uploaded file (has docId)
      if (fileToRemove?.docId) {
        // Mark this file as deleted for API
        setDeletedFileData((prev) => [...prev, { docId: fileToRemove.docId }]);
      } else {
        // Otherwise, if new file, remove from newFileData
        setNewFileData((prev) => prev.filter((_, i) => i !== index));
      }
    };

    // On remarks change for existing or new files
    const handleRemarkChange = (index: number, newRemark: string) => {
      setFileRemarks((prev) => ({ ...prev, [index]: newRemark }));
      setNewFileData((prev) =>
        prev.map((item, i) =>
          i === index
            ? {
                ...item,
                remarks: newRemark,
              }
            : item
        )
      );
    };

    const updateTimeLog = async (data: any) => {
      try {
        setDisableSubmit(true);

        // Convert select values to 24-hour format for comparison
        const to24Hour = (hour: string, minute: string, meridiem: string) => {
          let h = parseInt(hour);
          if (meridiem === "pm" && h !== 12) h += 12;
          if (meridiem === "am" && h === 12) h = 0;
          return h * 60 + parseInt(minute); // Convert to total minutes
        };

        const loginMinutes = to24Hour(startHour, startMinute, startMeridiem);
        const logoutMinutes = to24Hour(endHour, endMinute, endMeridiem);

        // Validate times
        if (loginMinutes === logoutMinutes) {
          messageApi.open({
            type: "error",
            content: "Logout time cannot be same as login time",
          });
          return;
        }

        if (logoutMinutes <= loginMinutes) {
          messageApi.open({
            type: "error",
            content: "Logout time must be after login time",
          });
          return;
        }

        // Format times for API (HH:mm:ss)
        const formatTimeForApi = (
          hour: string,
          minute: string,
          meridiem: string
        ) => {
          let h = parseInt(hour);
          if (meridiem === "pm" && h !== 12) h += 12;
          if (meridiem === "am" && h === 12) h = 0;
          return `${String(h).padStart(2, "0")}:${minute}:00`;
        };

        const loginTime = formatTimeForApi(
          startHour,
          startMinute,
          startMeridiem
        );
        const logoutTime = formatTimeForApi(endHour, endMinute, endMeridiem);

        const selectedEntity = combinedOptions.find(
          (opt) => opt.value === data.linkedEntity
        );

        // Get resource at site values from resourceForm
        const resourceFormValues = resourceForm.getFieldsValue();

        // Remove duplicates from selectedResourceKeys
        const uniqueSelectedKeys = selectedResourceKeys.filter(
          (key, index) => selectedResourceKeys.indexOf(key) === index
        );

        // Mandatory-field validation accumulator for selected resource/tool lines.
        const lineValidationErrors: string[] = [];

        const selectedActivityResources = uniqueSelectedKeys.map(
          (key: React.Key) => {
            const activityResourceId = Number(key);
            const actualResourceCount =
              resourceFormValues[`actualResourceCount_${key}`];
            const actualQuantity = resourceFormValues[`actualQuantity_${key}`];
            const actualRate = resourceFormValues[`actualRate_${key}`];
            // Find the resource data to get taskResourceId
            const resourceData = activityResourcesList.find(
              (r) => r.activityResourceId === activityResourceId
            );
            const label =
              resourceData?.laborTypeName || `Resource #${activityResourceId}`;

            if (
              actualResourceCount === undefined ||
              actualResourceCount === null ||
              actualResourceCount === "" ||
              Number(actualResourceCount) <= 0
            ) {
              lineValidationErrors.push(`${label}: Actual Count is required`);
            }
            if (!isValidDecimal(actualQuantity)) {
              lineValidationErrors.push(
                `${label}: valid Actual Quantity is required`
              );
            }
            if (!isValidDecimal(actualRate)) {
              lineValidationErrors.push(
                `${label}: valid Actual Rate is required`
              );
            }

            return {
              activityResourceId,
              taskResourceId: resourceData?.taskResourceId,
              resourceCount:
                actualResourceCount !== undefined &&
                actualResourceCount !== null &&
                actualResourceCount !== ""
                  ? Number(actualResourceCount)
                  : undefined, // Mark as undefined if empty
              actualQuantity: isValidDecimal(actualQuantity)
                ? Number(actualQuantity)
                : undefined,
              actualRate: isValidDecimal(actualRate)
                ? Number(actualRate)
                : undefined,
            };
          }
        );

        // Separate new and existing resources
        const newLines: Array<{
          taskResourceId: number;
          actualResourceCount: number;
          actualQuantity: number;
          actualRate: number;
        }> = [];
        const updatedLines: Array<{
          timeLogResourceId: number;
          actualResourceCount: number;
          actualQuantity: number;
          actualRate: number;
        }> = [];
        const deletedLines: Array<{ timeLogResourceId: number }> = [];

        // Separate resources with valid values and those without (empty inputs).
        // A line is valid only when count, quantity and rate are all present.
        const resourcesWithValidCount = selectedActivityResources.filter(
          (r) =>
            r.taskResourceId &&
            r.resourceCount !== undefined &&
            r.actualQuantity !== undefined &&
            r.actualRate !== undefined
        );
        const resourcesWithoutCount = selectedActivityResources.filter(
          (r) =>
            r.taskResourceId &&
            (r.resourceCount === undefined ||
              r.actualQuantity === undefined ||
              r.actualRate === undefined)
        );

        // Get task resource IDs that have valid counts
        const selectedTaskResourceIds = resourcesWithValidCount
          .map((r) => r.taskResourceId)
          .filter(Boolean);

        // Get task resource IDs that have empty inputs
        const emptyInputTaskResourceIds = resourcesWithoutCount
          .map((r) => r.taskResourceId)
          .filter(Boolean);

        // Find resources that were previously in the time log but are no longer selected
        // OR have empty input values (should be deleted)
        timeLogData?.timeLogResources?.forEach((existingResource: any) => {
          if (
            !selectedTaskResourceIds.includes(
              existingResource.taskResourceId
            ) ||
            emptyInputTaskResourceIds.includes(existingResource.taskResourceId)
          ) {
            deletedLines.push({
              timeLogResourceId: existingResource.timeLogResourceId,
            });
          }
        });

        // Only process resources that have valid counts
        resourcesWithValidCount.forEach((resource) => {
          if (
            resource.taskResourceId &&
            resource.resourceCount !== undefined &&
            resource.actualQuantity !== undefined &&
            resource.actualRate !== undefined
          ) {
            // Check if this resource already exists in the time log
            const existingTimeLogResource = timeLogData?.timeLogResources?.find(
              (tr: any) => tr.taskResourceId === resource.taskResourceId
            );

            if (existingTimeLogResource) {
              // Resource exists, add to updatedLines (check for duplicates first)
              const existingUpdatedLine = updatedLines.find(
                (line) =>
                  line.timeLogResourceId ===
                  existingTimeLogResource.timeLogResourceId
              );
              if (!existingUpdatedLine) {
                updatedLines.push({
                  timeLogResourceId: existingTimeLogResource.timeLogResourceId,
                  actualResourceCount: resource.resourceCount,
                  actualQuantity: resource.actualQuantity,
                  actualRate: resource.actualRate,
                });
              }
            } else {
              // Resource doesn't exist, add to newLines (check for duplicates first)
              const existingNewLine = newLines.find(
                (line) => line.taskResourceId === resource.taskResourceId
              );
              if (!existingNewLine) {
                newLines.push({
                  taskResourceId: resource.taskResourceId,
                  actualResourceCount: resource.resourceCount,
                  actualQuantity: resource.actualQuantity,
                  actualRate: resource.actualRate,
                });
              }
            }
          }
        });

        // Process Tools & Machinery similar to resources
        const toolFormValues = toolForm.getFieldsValue();
        const uniqueSelectedToolKeys = selectedToolKeys.filter(
          (key, index) => selectedToolKeys.indexOf(key) === index
        );

        const selectedActivityTools = uniqueSelectedToolKeys.map(
          (key: React.Key) => {
            const taskToolId = Number(key);
            const actualQuantity = toolFormValues[`actualQuantity_${key}`];
            const actualRate = toolFormValues[`actualRate_${key}`];
            // Find the tool data to get taskToolId
            const toolData = activityToolsList.find(
              (t) => t.taskToolId === taskToolId
            );
            const label = toolData?.toolName || `Tool #${taskToolId}`;

            if (!isValidDecimal(actualQuantity)) {
              lineValidationErrors.push(
                `${label}: valid Actual Quantity is required`
              );
            }
            if (!isValidDecimal(actualRate)) {
              lineValidationErrors.push(
                `${label}: valid Actual Rate is required`
              );
            }

            return {
              taskToolId,
              actualQuantity: isValidDecimal(actualQuantity)
                ? Number(actualQuantity)
                : undefined,
              actualRate: isValidDecimal(actualRate)
                ? Number(actualRate)
                : undefined,
            };
          }
        );

        // Separate new and existing tools
        const newToolLines: Array<{
          taskToolId: number;
          actualQuantity: number;
          actualRate: number;
        }> = [];
        const updatedToolLines: Array<{
          timeLogToolId: number;
          actualQuantity: number;
          actualRate: number;
        }> = [];
        const deletedToolLines: Array<{ timeLogToolId: number }> = [];

        // Separate tools with valid values and those without (empty inputs).
        // A tool line is valid only when quantity and rate are both present.
        const toolsWithValidCount = selectedActivityTools.filter(
          (t) =>
            t.taskToolId &&
            t.actualQuantity !== undefined &&
            t.actualRate !== undefined
        );
        const toolsWithoutCount = selectedActivityTools.filter(
          (t) =>
            t.taskToolId &&
            (t.actualQuantity === undefined || t.actualRate === undefined)
        );

        // Get task tool IDs that have valid counts
        const selectedTaskToolIds = toolsWithValidCount
          .map((t) => t.taskToolId)
          .filter(Boolean);

        // Get task tool IDs that have empty inputs
        const emptyInputTaskToolIds = toolsWithoutCount
          .map((t) => t.taskToolId)
          .filter(Boolean);

        // Find tools that were previously in the time log but are no longer selected
        // OR have empty input values (should be deleted)
        timeLogData?.timeLogTools?.forEach((existingTool: any) => {
          if (
            !selectedTaskToolIds.includes(existingTool.taskToolId) ||
            emptyInputTaskToolIds.includes(existingTool.taskToolId)
          ) {
            deletedToolLines.push({
              timeLogToolId: existingTool.timeLogToolId,
            });
          }
        });

        // Only process tools that have valid values
        toolsWithValidCount.forEach((tool) => {
          if (
            tool.taskToolId &&
            tool.actualQuantity !== undefined &&
            tool.actualRate !== undefined
          ) {
            // Check if this tool already exists in the time log
            const existingTimeLogTool = timeLogData?.timeLogTools?.find(
              (tt: any) => tt.taskToolId === tool.taskToolId
            );

            // Only consider materials for tools whose drawer the user touched.
            const currentRows = materialRowsByTool[tool.taskToolId];

            if (existingTimeLogTool) {
              // Tool exists, add to updatedLines (check for duplicates first)
              const existingUpdatedLine = updatedToolLines.find(
                (line) =>
                  line.timeLogToolId === existingTimeLogTool.timeLogToolId
              );
              if (!existingUpdatedLine) {
                const materialsDiff = currentRows
                  ? buildTimelogMaterialsDiff(
                      currentRows,
                      existingTimeLogTool.toolMaterials || [],
                    )
                  : null;
                updatedToolLines.push({
                  timeLogToolId: existingTimeLogTool.timeLogToolId,
                  actualQuantity: tool.actualQuantity,
                  actualRate: tool.actualRate,
                  ...(materialsDiff &&
                  timelogMaterialsDiffHasChanges(materialsDiff)
                    ? { toolMaterials: materialsDiff }
                    : {}),
                });
              }
            } else {
              // Tool doesn't exist, add to newLines (check for duplicates first)
              const existingNewLine = newToolLines.find(
                (line) => line.taskToolId === tool.taskToolId
              );
              if (!existingNewLine) {
                const newMaterials = currentRows
                  ? buildTimelogMaterialsDiff(currentRows, []).newLines
                  : [];
                newToolLines.push({
                  taskToolId: tool.taskToolId,
                  actualQuantity: tool.actualQuantity,
                  actualRate: tool.actualRate,
                  ...(newMaterials.length
                    ? { toolMaterials: newMaterials }
                    : {}),
                });
              }
            }
          }
        });

        // Block the update until every selected resource/tool line is valid.
        if (lineValidationErrors.length > 0) {
          resourceForm.validateFields().catch(() => {});
          toolForm.validateFields().catch(() => {});
          messageApi.open({ type: "error", content: lineValidationErrors[0] });
          return;
        }

        const payload: TimeLogUpdatePayload = {
          projectId: Number(currentProjectId),
          taskId: data.linkedTask ? Number(data.linkedTask) : undefined,
          timeLogId: timeLogData?.timeLogId,
          // issueId: String(data.linkedTask || ""),
          logTitle: selectedEntity
            ? selectedEntity.type === "task"
              ? selectedEntity.label.replace("[Task] ", "")
              : selectedEntity.label.replace("[Issue] ", "")
            : timeLogData.logTitle || "Time Log",
          logDate: data.logDate?.format("YYYY-MM-DD") || "",
          loginTime,
          logoutTime,
          billingType: (data.billingType || "billable").toLowerCase(),
          notes: data.notes || "",
          shiftname: data.shiftname || "",
          taskCompletionPercentage: data.taskCompletionPercentage
            ? Number(data.taskCompletionPercentage)
            : null,
          timeLogResources:
            newLines.length > 0 ||
            updatedLines.length > 0 ||
            deletedLines.length > 0
              ? {
                  newLines,
                  updatedLines,
                  deletedLines,
                }
              : undefined, // Add resources
          timeLogTools:
            newToolLines.length > 0 ||
            updatedToolLines.length > 0 ||
            deletedToolLines.length > 0
              ? {
                  newLines: newToolLines,
                  updatedLines: updatedToolLines,
                  deletedLines: deletedToolLines,
                }
              : undefined, // Add tools
        };

        // console.log("API Payload:", payload);

        if (selectedEntity?.type === "task") {
          payload.taskId = Number(data.linkedEntity);
        } else if (selectedEntity?.type === "issue") {
          payload.issueId = Number(data.linkedEntity);
        }

        // Prepare FormData for files along with payload
        const formData = new FormData();
        formData.append("taskTimelog", JSON.stringify(payload));

        const filesDataPayload = {
          newTimeLogFileData: newFileData.map((f, idx) => ({
            ...f,
            indexOfFileLocated: idx,
            action: "Created",
            remarks: fileRemarks[idx] || f.remarks,
          })),
          deletedTimeLogFileData: deletedFileData,
          existingFiles: uploadedFiles
            .filter((file) => file.docId !== undefined)
            .map((file) => ({
              docId: file.docId,
              docName: file.fileName,
              remarks:
                fileRemarks[uploadedFiles.indexOf(file)] || file.remarks || "",
            })),
        };

        formData.append("filesData", JSON.stringify(filesDataPayload));

        // Append new files
        newFileData.forEach((fileEntry, idx) => {
          const fileObj = uploadedFiles[fileEntry.indexOfFileLocated];
          if (fileObj instanceof File) {
            formData.append("files", fileObj);
          }
        });

        const response = await logHoursApis.updateTimeLogbyId(formData);
        // console.log("API Response:", response);

        if (newFileData.length === 0) {
          formData.append("files", "");
        } else {
          newFileData.forEach((fileEntry, idx) => {
            const fileObj = uploadedFiles[fileEntry.indexOfFileLocated];
            if (fileObj instanceof File) {
              formData.append("files", fileObj);
            }
          });
        }

        if (response?.status === true || response?.statusCode === 201) {
          const successMessage =
            response?.data?.message ||
            response?.message ||
            "Time log updated successfully";

          Swal.fire({
            icon: "success",
            title: "Success",
            text: successMessage,
            confirmButtonText: "Ok",
            customClass: {
              container: "border-class",
            },
          }).then(async () => {
            setIsEditMode(false);

            if (onBackToSummary && typeof onBackToSummary === "function") {
              onBackToSummary();
            }

            // Since API doesn't return updated time log data, fetch it from list API
            try {
              const currentTimeLogId = timeLogData?.timeLogId;

              // Call the list API to get updated data
              const listResponse = await logHoursApis.listTimeLog({
                pageSize: 10,
                direction: "P",
                sortOrder: "DESC",
                pageDiff: 0,
                paginationId: null,
                projectIds: timeLogData?.projectId,
                taskIds: timeLogData?.taskId || undefined,
                issueIds: timeLogData?.issueId || undefined,
              });

              if (listResponse?.status && listResponse?.data?.list) {
                // Find the updated time log in the list
                const updatedTimeLog = listResponse.data.list.find(
                  (log: any) => log.timeLogId === currentTimeLogId
                );

                if (updatedTimeLog) {
                  // Helper function to convert 24-hour time to 12-hour format
                  const to12HourTime = (time24: string) => {
                    if (!time24) return "";
                    const [h, m] = time24.split(":");
                    const hour = parseInt(h, 10);
                    const ampm = hour >= 12 ? "PM" : "AM";
                    const hour12 = hour % 12 || 12;
                    return `${String(hour12).padStart(2, "0")}:${m} ${ampm}`;
                  };

                  // Helper function to convert seconds to HH:MM
                  const secondsToHHMM = (seconds: number) => {
                    const hours = Math.floor(seconds / 3600);
                    const minutes = Math.floor((seconds % 3600) / 60);
                    return `${String(hours).padStart(2, "0")}:${String(
                      minutes
                    ).padStart(2, "0")}`;
                  };

                  // Filter timeLogResources to only include resources for this specific time log
                  // Backend returns all task resources, but we need only this time log's resources
                  const filteredTimeLogResources =
                    updatedTimeLog.timeLogResources?.filter(
                      (resource: any) =>
                        resource.timeLogId === updatedTimeLog.timeLogId
                    ) || [];

                  // Update Redux store with fresh data
                  const selectedLog = {
                    timeLogId: updatedTimeLog.timeLogId,
                    timeLogTitle: `Time Log #${updatedTimeLog.timeLogId}`,
                    projectId: updatedTimeLog.projectId,
                    projectTitle:
                      updatedTimeLog.projectName ||
                      `Project #${updatedTimeLog.projectId}`,
                    user: updatedTimeLog.createdByName || "—",
                    dailyLogHours: secondsToHHMM(
                      updatedTimeLog.totalWorkHoursInSec || 0
                    ),
                    timePeriod: `${to12HourTime(
                      updatedTimeLog.loginTime
                    )} - ${to12HourTime(updatedTimeLog.logoutTime)}`,
                    billingType: updatedTimeLog.billingType || "—",
                    notes: updatedTimeLog.notes || "—",
                    createdBy: updatedTimeLog.createdBy || "—",
                    createdAt: updatedTimeLog.createdAt || "—",
                    ...updatedTimeLog,
                    timeLogResources: filteredTimeLogResources, // Use filtered resources
                    totalWorkHoursInSec:
                      updatedTimeLog.totalWorkHoursInSec || 0,
                    loginTime: updatedTimeLog.loginTime,
                    logoutTime: updatedTimeLog.logoutTime,
                    logDate: updatedTimeLog.logDate,
                    orgId: updatedTimeLog.orgId,
                    taskId: updatedTimeLog.taskId,
                    taskName: updatedTimeLog.taskName,
                    userId: updatedTimeLog.userId,
                  };

                  dispatch(
                    updateTimeLogInDetailModalSlice({
                      selectedTimeLogDetails: selectedLog,
                      extra: {
                        createdAt: updatedTimeLog.createdAt,
                        totalWorkHoursInSec: updatedTimeLog.totalWorkHoursInSec,
                        billingType: updatedTimeLog.billingType,
                        dailyLogHours: secondsToHHMM(
                          updatedTimeLog.totalWorkHoursInSec || 0
                        ),
                        timePeriod: `${to12HourTime(
                          updatedTimeLog.loginTime
                        )} - ${to12HourTime(updatedTimeLog.logoutTime)}`,
                        notes: updatedTimeLog.notes,
                        shiftName: updatedTimeLog.shiftName,
                        approvalStatus: updatedTimeLog.approvalStatus,
                      },
                    })
                  );
                } else {
                }
              }

              // Call task completion update callback if completion percentage was updated
              if (
                onTaskCompletionUpdate &&
                typeof onTaskCompletionUpdate === "function"
              ) {
                onTaskCompletionUpdate();
              }

              // Notify parent so it can refresh its own state and (in detail
              // page) switch viewMode back to "summary". Without this, the
              // detail page stays in edit mode after a successful update.
              if (
                refreshTimeLogList &&
                typeof refreshTimeLogList === "function"
              ) {
                await refreshTimeLogList();
              }
            } catch (error) {
              console.error("Error fetching updated time log data:", error);

              // Still refresh the list even if fetching fails
              if (
                refreshTimeLogList &&
                typeof refreshTimeLogList === "function"
              ) {
                await refreshTimeLogList();
              }

              // Call task completion update callback if completion percentage was updated
              if (
                onTaskCompletionUpdate &&
                typeof onTaskCompletionUpdate === "function"
              ) {
                onTaskCompletionUpdate();
              }
            }
          });
          return;
        }

        const errorMessage =
          response?.message ||
          response?.data?.message ||
          "Failed to update time log";
        message.error(errorMessage);
      } catch (error: unknown) {
        console.error("Update Error:", error);

        let errorMessage = "An unexpected error occurred. Please try again.";

        if (typeof error === "object" && error !== null) {
          const apiError = error as {
            response?: { data?: { message?: string } };
          };
          errorMessage =
            apiError?.response?.data?.message ||
            (error as Error)?.message ||
            errorMessage;
        }

        message.error(errorMessage);
      } finally {
        setDisableSubmit(false);
      }
    };

    if (loading) {
      return (
        <div
          style={{
            position: "relative",
            minHeight: "200px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255, 255, 255, 0.8)",
          }}
        >
          <Spin indicator={<LoadingOutlined spin />} size="large" />
        </div>
      );
    }

    const ApprovalTimelineContent = () => {
      const approvalsData = timeLogData?.stateApprovals;
      if (!approvalsData || approvalsData?.length === 0) {
        return (
          <span
            style={{
              display: "inline-block",
              padding: "2px 8px",
              borderRadius: "4px",
              backgroundColor: "#fff",
              border: "1px solid #e85347",
              color: "#e85347",
              fontSize: "10.5px",
              fontWeight: "500",
            }}
          >
            No
          </span>
        );
      }

      return (
        <Popover
          content={
            <Timeline className="approval-timeline">
              {approvalsData?.map((approval: any, index: number) => {
                const isApproved = approval.status === "Approved";
                const isRejected = approval.status === "Rejected";
                return (
                  <Timeline.Item
                    key={index}
                    dot={
                      isApproved ? (
                        <CheckCircleOutlined style={{ color: "green" }} />
                      ) : isRejected ? (
                        <CloseCircleOutlined style={{ color: "red" }} />
                      ) : undefined
                    }
                  >
                    <div style={{ marginBottom: "4px" }}>
                      <strong style={{ color: "#2557d6" }}>
                        {approval?.userName ||
                          `User ${approval?.userId || "Unknown"}`}
                      </strong>
                    </div>
                    {approval?.roleName && (
                      <div
                        className="designation"
                        style={{ color: "#666", fontSize: "12px" }}
                      >
                        Role: {approval.roleName}
                      </div>
                    )}
                    {approval?.approvalStage && (
                      <div
                        className="designation"
                        style={{ color: "#666", fontSize: "12px" }}
                      >
                        Stage: {approval.approvalStage}
                      </div>
                    )}
                    {approval?.level && (
                      <div
                        className="designation"
                        style={{ color: "#666", fontSize: "12px" }}
                      >
                        Level: {approval.level}
                      </div>
                    )}
                    {approval?.status && (
                      <div
                        className="designation"
                        style={{ color: "#666", fontSize: "12px" }}
                      >
                        Status: {approval.status}
                      </div>
                    )}
                    {approval?.actionDate && (
                      <div
                        className="designation"
                        style={{ color: "#666", fontSize: "12px" }}
                      >
                        {dayjs(approval.actionDate).format("DD-MMM-YYYY")}
                      </div>
                    )}
                  </Timeline.Item>
                );
              })}
            </Timeline>
          }
          title={<span style={{ color: "#2557d6" }}>Approval Hierarchy</span>}
        >
          <span
            style={{
              color: "#2557d6",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            Yes
          </span>
        </Popover>
      );
    };

    const FieldDisplay = ({
      heading,
      value,
      className = "",
    }: {
      heading: string;
      // value: string;
      value: ReactNode;
      className?: string;
    }) => (
      <div className={`d-flex mb-2 ${className}`}>
        <span
          className="fw-semibold me-2"
          style={{
            minWidth: 160,
            color: "#333",
            fontWeight: 600,
          }}
        >
          {heading}
        </span>
        <span className="text-muted">{value}</span>
      </div>
    );

    const formatDate = (date?: string | Date | dayjs.Dayjs | null): string => {
      if (!date) return "—";

      // If it's already a Dayjs object
      if (dayjs.isDayjs(date)) {
        return date.format("DD-MMM-YYYY");
      }

      // For string or Date objects
      const jsDate = new Date(date);
      return jsDate.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    };

    const getLabel = (options: any[], value: any) =>
      options?.find((opt) => opt.value === value)?.label ?? "";

    return (
      <>
        <Card
          style={{
            borderRadius: "2px",
            border: "1px solid #f4efefff",
            padding: "0",
          }}
        >
          <div
            style={{
              padding: "20px 20px 10px 20px",
              paddingTop: "10px",
              background: "#fff",
              borderRadius: "8px",
              position: "relative",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2 pe-2 ms-auto">
                {!isEditMode && !isFromWorkUI && (
                  <li style={{ position: "absolute", top: 10, right: 20 }}>
                    <ViewAccess moduleName="Projects">
                      {canEditTimeLog && (
                        <UncontrolledDropdown>
                          <DropdownToggle
                            tag="a"
                            className="dropdown-toggle btn btn-icon btn-trigger"
                          >
                            <Icon name="more-v" />
                          </DropdownToggle>
                          <DropdownMenu end>
                            <ul className="link-list-opt">
                              {/* {canEditTimeLog && ( */}
                              <li>
                                <DropdownItem
                                  tag="a"
                                  href="#editTimeLog"
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    setIsEditMode(true);

                                    // Initialize form with existing data when entering edit mode
                                    if (timeLogData) {
                                      const initialLinkedEntity =
                                        timeLogData.taskId
                                          ? {
                                              value: timeLogData.taskId,
                                              type: "task",
                                            }
                                          : timeLogData.issueId
                                          ? {
                                              value: timeLogData.issueId,
                                              type: "issue",
                                            }
                                          : null;
                                      reset({
                                        linkedEntity:
                                          initialLinkedEntity?.value || null,
                                        user: timeLogData?.createdByName || "",
                                        logDate: parseApiDate(
                                          timeLogData?.logDate
                                        ),
                                        billingType:
                                          timeLogData?.billingType?.toLowerCase() ||
                                          "",
                                        notes: timeLogData?.notes || "",
                                        shiftname: timeLogData?.shiftName || "",
                                        taskCompletionPercentage:
                                          timeLogData?.taskCompletionPercentage ||
                                          null,
                                      });
                                    }
                                  }}
                                >
                                  <span>Edit</span>
                                </DropdownItem>
                              </li>
                              {/* )} */}
                            </ul>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      )}
                    </ViewAccess>
                  </li>
                )}

                {isEditMode && (
                  <>
                    <li>
                      <Button
                        className="btn btn-primary btn-sm"
                        type="submit"
                        color="primary"
                        onClick={handleSubmit(updateTimeLog)}
                        disabled={disableSubmit}
                      >
                        <Icon name="save" /> <span>Save</span>
                      </Button>
                    </li>
                    <li>
                      {/* <CancelAccess moduleName="TimeSheets"> */}
                      <Button
                        className="btn btn-secondary btn-sm"
                        type="button"
                        color="secondary"
                        onClick={() => {
                          Swal.fire({
                            title: "Are you sure?",
                            text: "You won't be able to revert this!",
                            icon: "warning",
                            showCancelButton: true,
                            confirmButtonColor: "#3085d6",
                            cancelButtonColor: "#d33",
                            confirmButtonText: "Yes, cancel it!",
                            customClass: {
                              container: "border-class", // Add a custom class
                            },
                          }).then((result) => {
                            if (result.isConfirmed) {
                              Swal.fire({
                                title: "Cancelled",
                                icon: "success",
                                text: "Your changes have been cancelled.",
                                customClass: {
                                  container: "border-class", // Add a custom class
                                },
                              });
                              setIsEditMode(false);
                              reset(); // rollback form values
                              if (onBackToSummary) onBackToSummary();
                              // Re-initialize values from timeLogData if available after cancel
                              if (timeLogData) {
                                reset({
                                  linkedTask: timeLogData?.taskId ?? "",
                                  user: timeLogData?.userId ?? "",
                                  // logDate: timeLogData?.logDate
                                  //   ? String(timeLogData?.logDate)
                                  //   : undefined,
                                  logDate: parseApiDate(timeLogData?.logDate),
                                  billingType: timeLogData?.billingType ?? "",
                                  notes: timeLogData?.notes ?? "",
                                  taskCompletionPercentage:
                                    timeLogData?.taskCompletionPercentage ??
                                    null,
                                });

                                // Reset time pickers as well
                                if (
                                  timeLogData.loginTime &&
                                  timeLogData.logoutTime
                                ) {
                                  const parseTime = (t: string) => {
                                    const [h, m] = t.split(":");
                                    const hr = parseInt(h);
                                    const min = parseInt(m);

                                    let hour12 = hr % 12 || 12;
                                    const meridiem = hr < 12 ? "am" : "pm";

                                    return {
                                      hour: hour12.toString().padStart(2, "0"),
                                      min: min.toString().padStart(2, "0"),
                                      meridiem: meridiem,
                                    };
                                  };

                                  const start = parseTime(
                                    timeLogData.loginTime
                                  );
                                  const end = parseTime(timeLogData.logoutTime);

                                  setStartHour(start.hour);
                                  setStartMinute(start.min);
                                  setStartMeridiem(start.meridiem);
                                  setEndHour(end.hour);
                                  setEndMinute(end.min);
                                  setEndMeridiem(end.meridiem);

                                  setSelectedStartTime(
                                    `${start.hour}:${start.min} ${start.meridiem}`
                                  );
                                  setSelectedEndTime(
                                    `${end.hour}:${end.min} ${end.meridiem}`
                                  );
                                  setDailyLogHours(
                                    calculateHours(
                                      `${start.hour}:${start.min} ${start.meridiem}`,
                                      `${end.hour}:${end.min} ${end.meridiem}`
                                    )
                                  );
                                }
                              }
                            }
                          });
                        }}
                      >
                        <span>Cancel</span>
                      </Button>
                      {/* </CancelAccess> */}
                    </li>
                  </>
                )}
              </ul>
            </div>

            {contextHolder}
            <FormProvider {...methods}>
              <form className="content clearfix mt-2">
                <div className="row g-3">
                  {/* Link Task/Issue */}
                  <div style={{ width: "50%", paddingRight: 24 }}>
                    {isEditMode ? (
                      <InlineFieldEdit
                        label="Link Task/Issue"
                        name="linkedEntity"
                        isEditMode={isEditMode}
                        isRequiredField={true}
                      >
                        <FormSelect
                          name="linkedEntity"
                          label=""
                          placeholder="Select Task/Issue"
                          options={combinedOptions}
                          required={true}
                          onChange={(value) => {
                            const entity = combinedOptions.find(
                              (opt) => opt.value === value
                            );
                            if (entity) {
                              setValue("linkedEntity", value);
                            }
                          }}
                          disabled={true}
                        />
                      </InlineFieldEdit>
                    ) : (
                      <FieldDisplay
                        heading="Link Task/Issue"
                        // value={
                        //   combinedOptions.find(
                        //     (opt) =>
                        //       opt.value ===
                        //       (timeLogData.taskId || timeLogData.issueId)
                        //   )?.label || "—"
                        // }
                        value={
                          combinedOptions.find(
                            (opt) =>
                              opt.value ===
                              (timeLogData.taskId || timeLogData.issueId)
                          )?.label ||
                          (timeLogData.moduleType === "Task"
                            ? `[Task] ${timeLogData.taskName || "—"}`
                            : timeLogData.moduleType === "Issue"
                            ? `[Issue] ${timeLogData.issueName || "—"}`
                            : "—")
                        }
                      />
                    )}
                  </div>
                  {/* User */}
                  <div style={{ width: "50%", paddingRight: 24 }}>
                    {isEditMode ? (
                      <InlineFieldEdit
                        label="User"
                        name="user"
                        isEditMode={isEditMode}
                      >
                        <Input
                          value={timeLogData?.createdByName || ""}
                          disabled
                        />
                      </InlineFieldEdit>
                    ) : (
                      <FieldDisplay
                        heading="User"
                        value={
                          getLabel(projectUsers, watch("user")) ||
                          timeLogData?.createdByName ||
                          "—"
                        }
                      />
                    )}
                  </div>
                  {/* Date */}
                  <div style={{ width: "50%", paddingRight: 24 }}>
                    {isEditMode ? (
                      <InlineFieldEdit
                        label="Date"
                        name="logDate"
                        isEditMode={isEditMode}
                      >
                        <FormDatePicker
                          name="logDate"
                          label=""
                          placeholder="01-Jul-2025"
                          // required={true}
                        />
                      </InlineFieldEdit>
                    ) : (
                      <FieldDisplay
                        heading="Date"
                        value={formatDate(watch("logDate")) || "—"}
                      />
                    )}
                  </div>
                  {/* Billing Type */}
                  <div style={{ width: "50%", paddingRight: 24 }}>
                    {isEditMode ? (
                      <InlineFieldEdit
                        label="Billing Type"
                        name="billingType" // Added name for FormSelect
                        isEditMode={isEditMode}
                      >
                        <FormSelect
                          name="billingType"
                          label=""
                          placeholder="Select"
                          options={[
                            { label: "Billable", value: "billable" },
                            { label: "Non-Billable", value: "nonbillable" },
                          ]}
                        />
                      </InlineFieldEdit>
                    ) : (
                      <FieldDisplay
                        heading="Billing Type"
                        value={
                          getLabel(
                            [
                              { label: "Billable", value: "billable" },
                              { label: "Non-Billable", value: "nonbillable" },
                            ],
                            watch("billingType")
                          ) || "—"
                        }
                      />
                    )}
                  </div>
                  {/* Daily Log — own full-width row */}
                  <div
                    style={{
                      width: "100%",
                      paddingRight: 24,
                      marginBottom: 32,
                    }}
                  >
                    {isEditMode ? (
                      <div
                        className="d-flex align-items-center"
                        style={{ gap: "12px", minHeight: "40px", fontSize: "13px", width: "100%" }}
                      >
                        <span
                          className="fw-semibold text-dark"
                          style={{ minWidth: 140 }}
                        >
                          Daily Log
                          <span className="text-danger ms-1">*</span>
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="d-flex gap-3" style={{ width: "100%" }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <label className="form-label">Start Time</label>
                              <div className="d-flex gap-1">
                                <Select
                                  value={startHour}
                                  onChange={(val) => setStartHour(val)}
                                  options={hours.map((h) => ({
                                    value: h,
                                    label: h,
                                  }))}
                                  style={{ flex: 1, minWidth: 0 }}
                                />
                                <Select
                                  value={startMinute}
                                  onChange={(val) => setStartMinute(val)}
                                  options={minutes.map((m) => ({
                                    value: m,
                                    label: m,
                                  }))}
                                  style={{ flex: 1, minWidth: 0 }}
                                />
                                <Select
                                  value={startMeridiem}
                                  onChange={(val) => setStartMeridiem(val)}
                                  options={meridiems.map((m) => ({
                                    value: m,
                                    label: m.toUpperCase(),
                                  }))}
                                  style={{ flex: 1, minWidth: 0 }}
                                />
                              </div>
                            </div>

                            <div style={{ flex: 1, minWidth: 0 }}>
                              <label className="form-label">End Time</label>
                              <div className="d-flex gap-1">
                                <Select
                                  value={endHour}
                                  onChange={(val) => setEndHour(val)}
                                  options={hours.map((h) => ({
                                    value: h,
                                    label: h,
                                  }))}
                                  style={{ flex: 1, minWidth: 0 }}
                                />
                                <Select
                                  value={endMinute}
                                  onChange={(val) => setEndMinute(val)}
                                  options={minutes.map((m) => ({
                                    value: m,
                                    label: m,
                                  }))}
                                  style={{ flex: 1, minWidth: 0 }}
                                />
                                <Select
                                  value={endMeridiem}
                                  onChange={(val) => setEndMeridiem(val)}
                                  options={meridiems.map((m) => ({
                                    value: m,
                                    label: m.toUpperCase(),
                                  }))}
                                  style={{ flex: 1, minWidth: 0 }}
                                />
                              </div>
                            </div>

                            <div style={{ flex: 1, minWidth: 0 }}>
                              <label className="form-label">Total Hours</label>
                              <input
                                className="form-control"
                                value={calculateHours(
                                  `${startHour}:${startMinute} ${startMeridiem}`,
                                  `${endHour}:${endMinute} ${endMeridiem}`
                                )}
                                disabled
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <FieldDisplay
                        heading="Daily Log"
                        value={
                          selectedStartTime && selectedEndTime
                            ? `${selectedStartTime} - ${selectedEndTime} (${dailyLogHours})`
                            : "—"
                        }
                      />
                    )}
                  </div>
                  {/* shiftname — next row, 50% */}
                  <div style={{ width: "50%", paddingRight: 24 }}>
                    {isEditMode ? (
                      <InlineFieldEdit
                        label="Shift Name"
                        name="shiftname"
                        isEditMode={isEditMode}
                      >
                        <FormSelect
                          name="shiftname"
                          label=""
                          placeholder="Select"
                          options={shiftOptions}
                          defaultValue={timeLogData?.shiftName}
                        />
                      </InlineFieldEdit>
                    ) : (
                      <FieldDisplay
                        heading="Shift Name"
                        // value={
                        //   watch("shiftname") || timeLogData?.shiftName || "—"
                        // }
                        value={timeLogData?.shiftName || "—"}
                      />
                    )}
                  </div>
                  {/* Approval Required — next row, 50% */}
                  <div style={{ width: "50%", paddingRight: 24 }}>
                    {isEditMode ? (
                      <InlineFieldEdit
                        label="Approval Required"
                        name="approvalRequired"
                        isEditMode={isEditMode}
                      >
                        <Select
                          style={{ width: "100%" }}
                          value={timeLogData?.approvalRequired ? "yes" : "no"}
                          disabled={true}
                          options={[
                            { label: "Yes", value: "yes" },
                            { label: "No", value: "no" },
                          ]}
                        />
                      </InlineFieldEdit>
                    ) : (
                      <FieldDisplay
                        heading="Approval Required"
                        value={
                          timeLogData?.approvalRequired ? (
                            <ApprovalTimelineContent />
                          ) : (
                            <span
                              style={{
                                display: "inline-block",
                                padding: "2px 8px",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                border: "1px solid #e85347",
                                color: "#e85347",
                                fontSize: "10.5px",
                                fontWeight: "500",
                              }}
                            >
                              No
                            </span>
                          )
                        }
                      />
                    )}
                  </div>
                  {/* Resource Allocation - Only for tasks */}
                  {timeLogData?.taskId && (
                    <div className="col-12">
                      <div className="form-group">
                        <h6 className="section-header">Allocated Resources</h6>
                        {isEditMode ? (
                          <div className="mt-2">
                            <Form
                              form={resourceForm}
                              component={false}
                              onValuesChange={() => forceRecalc()}
                            >
                              <Table
                                rowKey={(record) =>
                                  `${record.activityResourceId}`
                                }
                                columns={[
                                  {
                                    title: "Labour Type",
                                    dataIndex: "laborTypeName",
                                    key: "laborTypeName",
                                    width: 160,
                                    align: "center",
                                  },
                                  {
                                    title: "Labour Category",
                                    dataIndex: "laborCategory",
                                    key: "laborCategory",
                                    width: 160,
                                    align: "center",
                                  },
                                  {
                                    title: "Resource Count",
                                    dataIndex: "resourceCount",
                                    key: "resourceCount",
                                    width: 120,
                                    align: "center",
                                    render: (text: any) => text || "",
                                  },
                                  {
                                    title: "Planned Qty",
                                    dataIndex: "plannedQuantity",
                                    key: "plannedQuantity",
                                    width: 110,
                                    align: "center",
                                    render: (text: any, record: any) => {
                                      if (
                                        text === undefined ||
                                        text === null ||
                                        text === ""
                                      )
                                        return "-";
                                      const uom = resolveUomName(record);
                                      return `${text}${uom ? ` ${uom}` : ""}`;
                                    },
                                  },
                                  {
                                    title: "Planned Rate",
                                    dataIndex: "plannedRate",
                                    key: "plannedRate",
                                    width: 110,
                                    align: "center",
                                    render: (text: any) =>
                                      text !== undefined &&
                                      text !== null &&
                                      text !== ""
                                        ? text
                                        : "-",
                                  },
                                  {
                                    title: "Resource at Site",
                                    dataIndex: "actualResourceCount",
                                    key: "actualResourceCount",
                                    width: 130,
                                    align: "center",
                                    onCell: (record: any) => ({
                                      record,
                                      dataIndex: "actualResourceCount",
                                      title: "Resource at Site",
                                      form: resourceForm,
                                      editing: isResourceEditing(record),
                                    }),
                                    render: (text: any) => text || "",
                                  },
                                  {
                                    title: "Actual Quantity",
                                    dataIndex: "actualQuantity",
                                    key: "actualQuantity",
                                    width: 130,
                                    align: "center",
                                    onCell: (record: any) => ({
                                      record,
                                      dataIndex: "actualQuantity",
                                      title: "Actual Quantity",
                                      form: resourceForm,
                                      editing: isResourceEditing(record),
                                    }),
                                    render: (text: any) => text || "",
                                  },
                                  {
                                    title: "Actual Rate",
                                    dataIndex: "actualRate",
                                    key: "actualRate",
                                    width: 130,
                                    align: "center",
                                    onCell: (record: any) => ({
                                      record,
                                      dataIndex: "actualRate",
                                      title: "Actual Rate",
                                      form: resourceForm,
                                      editing: isResourceEditing(record),
                                    }),
                                    render: (text: any) => text || "",
                                  },
                                  {
                                    title: "Total Amount",
                                    key: "resourceTotalAmount",
                                    width: 140,
                                    align: "center",
                                    render: (_: any, record: any) => {
                                      const key = `${record.activityResourceId}`;
                                      if (
                                        !selectedResourceKeys
                                          .map(String)
                                          .includes(key)
                                      )
                                        return "";
                                      const total = resourceLineTotal(
                                        resourceForm.getFieldValue(
                                          `actualResourceCount_${key}`
                                        ),
                                        resourceForm.getFieldValue(
                                          `actualQuantity_${key}`
                                        ),
                                        resourceForm.getFieldValue(
                                          `actualRate_${key}`
                                        )
                                      );
                                      return total ? formatCurrency(total) : "";
                                    },
                                  },
                                ]}
                                rowSelection={{
                                  selectedRowKeys: selectedResourceKeys,
                                  onChange: onResourceSelectChange,
                                }}
                                components={{
                                  body: {
                                    cell: ResourceEditableCell,
                                  },
                                }}
                                dataSource={activityResourcesList.map(
                                  (resource: any) => {
                                    // Find matching timeLogResource to get actuals
                                    const timeLogResource =
                                      timeLogData?.timeLogResources?.find(
                                        (tr: any) =>
                                          tr.taskResourceId ===
                                          resource.taskResourceId
                                      );
                                    return {
                                      ...resource,
                                      resourceCount:
                                        timeLogResource?.resourceCount ||
                                        resource.resourceCount ||
                                        0,
                                      actualResourceCount:
                                        timeLogResource?.actualResourceCount ||
                                        0,
                                      actualQuantity:
                                        timeLogResource?.actualQuantity !==
                                          undefined &&
                                        timeLogResource?.actualQuantity !== null
                                          ? String(
                                              timeLogResource.actualQuantity
                                            )
                                          : undefined,
                                      actualRate:
                                        timeLogResource?.actualRate !==
                                          undefined &&
                                        timeLogResource?.actualRate !== null
                                          ? String(timeLogResource.actualRate)
                                          : undefined,
                                    };
                                  }
                                )}
                                pagination={false}
                                bordered
                                size="middle"
                                scroll={{ x: 1100 }}
                              />
                            </Form>
                            {activityResourcesList.length > 0 && (
                              <SectionTotalRow
                                label="Resources Total"
                                amount={computeResourceSectionTotalEdit()}
                              />
                            )}
                            {activityResourcesList.length === 0 && (
                              <div
                                style={{
                                  textAlign: "center",
                                  padding: "20px",
                                  color: "#666",
                                }}
                              >
                                No resources available for this task. Please
                                check if the task has an associated activity
                                with resources.
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="mt-3">
                            <div className="table-responsive poTable">
                              <Table
                                className="customTable"
                                rowKey={(record: any) =>
                                  `${record.activityResourceId}`
                                }
                                columns={[
                                  {
                                    title: "Labour Type",
                                    dataIndex: "laborTypeName",
                                    key: "laborTypeName",
                                    width: 160,
                                    align: "center",
                                  },
                                  {
                                    title: "Labour Category",
                                    dataIndex: "laborCategory",
                                    key: "laborCategory",
                                    width: 160,
                                    align: "center",
                                  },
                                  {
                                    title: "Resource Count",
                                    dataIndex: "resourceCount",
                                    key: "resourceCount",
                                    width: 120,
                                    align: "center",
                                  },
                                  {
                                    title: "Resource at Site",
                                    dataIndex: "actualResourceCount",
                                    key: "actualResourceCount",
                                    width: 130,
                                    align: "center",
                                    render: (text: any) => text || "",
                                  },
                                  {
                                    title: "Actual Quantity",
                                    dataIndex: "actualQuantity",
                                    key: "actualQuantity",
                                    width: 130,
                                    align: "center",
                                    render: (text: any) =>
                                      text !== undefined &&
                                      text !== null &&
                                      text !== ""
                                        ? text
                                        : "-",
                                  },
                                  {
                                    title: "Actual Rate",
                                    dataIndex: "actualRate",
                                    key: "actualRate",
                                    width: 130,
                                    align: "center",
                                    render: (text: any) =>
                                      text !== undefined &&
                                      text !== null &&
                                      text !== ""
                                        ? text
                                        : "-",
                                  },
                                  {
                                    title: "Total Amount",
                                    key: "resourceTotalAmount",
                                    width: 140,
                                    align: "center",
                                    render: (_: any, record: any) => {
                                      const total = resourceLineTotal(
                                        record.actualResourceCount,
                                        record.actualQuantity,
                                        record.actualRate
                                      );
                                      return total ? formatCurrency(total) : "-";
                                    },
                                  },
                                ]}
                                dataSource={(() => {
                                  if (!timeLogData?.timeLogResources) return [];

                                  // Remove duplicates based on taskResourceId
                                  const uniqueResources =
                                    timeLogData.timeLogResources.reduce(
                                      (acc: any[], current: any) => {
                                        const existingIndex = acc.findIndex(
                                          (item: any) =>
                                            item.taskResourceId ===
                                            current.taskResourceId
                                        );
                                        if (existingIndex === -1) {
                                          acc.push(current);
                                        }
                                        return acc;
                                      },
                                      []
                                    );

                                  return uniqueResources
                                    .map((timeLogResource: any) => {
                                      // Find the matching activity resource to get the full details
                                      const matchingResource =
                                        activityResourcesList.find(
                                          (activityResource: any) =>
                                            activityResource.taskResourceId ===
                                            timeLogResource.taskResourceId
                                        );
                                      return matchingResource
                                        ? {
                                            ...matchingResource,
                                            resourceCount:
                                              timeLogResource.resourceCount ||
                                              0, // Show planned/allocated count
                                            actualResourceCount:
                                              timeLogResource.actualResourceCount, // Show actual resources at site
                                            actualQuantity:
                                              timeLogResource.actualQuantity,
                                            actualRate:
                                              timeLogResource.actualRate,
                                          }
                                        : null;
                                    })
                                    .filter(Boolean);
                                })()}
                                scroll={{ x: "max-content" }}
                                bordered
                                size="middle"
                                pagination={false}
                                style={{ minWidth: "600px" }}
                              />
                            </div>
                            <SectionTotalRow
                              label="Resources Total"
                              amount={computeResourceSectionTotalView()}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Tools & Machinery - Only for tasks */}
                  {timeLogData?.taskId && (
                    <div className="col-12">
                      <div className="form-group">
                        <h6 className="section-header">Tools & Machinery</h6>
                        {isEditMode ? (
                          <div className="mt-2">
                            <Form
                              form={toolForm}
                              component={false}
                              onValuesChange={() => forceRecalc()}
                            >
                              <Table
                                rowKey={(record) => `${record.taskToolId}`}
                                columns={[
                                  {
                                    title: "Tool Type",
                                    dataIndex: "toolTypeName",
                                    key: "toolTypeName",
                                    width: 150,
                                    align: "center",
                                  },
                                  {
                                    title: "Tool/Machinery",
                                    dataIndex: "toolName",
                                    key: "toolName",
                                    width: 150,
                                    align: "center",
                                  },
                                  {
                                    title: "Serial Number",
                                    dataIndex: "serialNumber",
                                    key: "serialNumber",
                                    width: 130,
                                    align: "center",
                                    render: (text: any) =>
                                      text !== undefined &&
                                      text !== null &&
                                      text !== ""
                                        ? text
                                        : "-",
                                  },
                                  // Count column hidden for tools in timelogs
                                  // {
                                  //   title: "Count",
                                  //   dataIndex: "count",
                                  //   key: "count",
                                  //   width: 100,
                                  //   align: "center",
                                  //   render: (text: any) => text || "",
                                  // },
                                  {
                                    title: "Planned Qty",
                                    dataIndex: "plannedQuantity",
                                    key: "plannedQuantity",
                                    width: 110,
                                    align: "center",
                                    render: (text: any, record: any) => {
                                      if (
                                        text === undefined ||
                                        text === null ||
                                        text === ""
                                      )
                                        return "-";
                                      const uom = resolveUomName(record);
                                      return `${text}${uom ? ` ${uom}` : ""}`;
                                    },
                                  },
                                  {
                                    title: "Planned Rate",
                                    dataIndex: "plannedRate",
                                    key: "plannedRate",
                                    width: 110,
                                    align: "center",
                                    render: (text: any) =>
                                      text !== undefined &&
                                      text !== null &&
                                      text !== ""
                                        ? text
                                        : "-",
                                  },
                                  {
                                    title: "Actual Quantity",
                                    dataIndex: "actualQuantity",
                                    key: "actualQuantity",
                                    width: 130,
                                    align: "center",
                                    onCell: (record: any) => ({
                                      record,
                                      dataIndex: "actualQuantity",
                                      title: "Actual Quantity",
                                      form: toolForm,
                                      editing: isToolEditing(record),
                                    }),
                                    render: (text: any) => text || "",
                                  },
                                  {
                                    title: "Actual Rate",
                                    dataIndex: "actualRate",
                                    key: "actualRate",
                                    width: 130,
                                    align: "center",
                                    onCell: (record: any) => ({
                                      record,
                                      dataIndex: "actualRate",
                                      title: "Actual Rate",
                                      form: toolForm,
                                      editing: isToolEditing(record),
                                    }),
                                    render: (text: any) => text || "",
                                  },
                                  {
                                    title: "Total Amount",
                                    key: "toolTotalAmount",
                                    width: 140,
                                    align: "center",
                                    render: (_: any, record: any) => {
                                      const key = `${record.taskToolId}`;
                                      if (
                                        !selectedToolKeys
                                          .map(String)
                                          .includes(key)
                                      )
                                        return "";
                                      const total = toolLineTotal(
                                        toolForm.getFieldValue(
                                          `actualQuantity_${key}`
                                        ),
                                        toolForm.getFieldValue(
                                          `actualRate_${key}`
                                        )
                                      );
                                      return total ? formatCurrency(total) : "";
                                    },
                                  },
                                  {
                                    title: "Materials",
                                    key: "toolMaterials",
                                    width: 150,
                                    align: "center",
                                    render: (_: any, record: any) => {
                                      const id = record.taskToolId;
                                      const selected = selectedToolKeys
                                        .map(String)
                                        .includes(`${id}`);
                                      const planned = (
                                        record.toolMaterials || []
                                      ).length;
                                      const rows = getToolMaterialRows(id);
                                      const filled = rows.filter(
                                        (r) =>
                                          r.actualQuantity != null &&
                                          `${r.actualQuantity}` !== "",
                                      ).length;
                                      return (
                                        <Button
                                          type="button"
                                          color="primary"
                                          size="sm"
                                          outline={!filled}
                                          disabled={!selected || !planned}
                                          title={
                                            !planned
                                              ? "No planned materials on this tool"
                                              : selected
                                              ? "Log actual material usage"
                                              : "Select this tool first"
                                          }
                                          onClick={() =>
                                            setMaterialsDrawerToolId(id)
                                          }
                                        >
                                          <em className="icon ni ni-list" />{" "}
                                          {planned
                                            ? `Materials (${filled}/${planned})`
                                            : "No Materials"}
                                        </Button>
                                      );
                                    },
                                  },
                                ]}
                                rowSelection={{
                                  selectedRowKeys: selectedToolKeys,
                                  onChange: onToolSelectChange,
                                }}
                                components={{
                                  body: {
                                    cell: ToolEditableCell,
                                  },
                                }}
                                dataSource={activityToolsList.map(
                                  (tool: any) => {
                                    // Find matching timeLogTool to get actuals
                                    const timeLogTool =
                                      timeLogData?.timeLogTools?.find(
                                        (tt: any) =>
                                          tt.taskToolId === tool.taskToolId
                                      );
                                    return {
                                      ...tool,
                                      count: tool.count || 0,
                                      actualQuantity:
                                        timeLogTool?.actualQuantity !==
                                          undefined &&
                                        timeLogTool?.actualQuantity !== null
                                          ? String(timeLogTool.actualQuantity)
                                          : undefined,
                                      actualRate:
                                        timeLogTool?.actualRate !== undefined &&
                                        timeLogTool?.actualRate !== null
                                          ? String(timeLogTool.actualRate)
                                          : undefined,
                                    };
                                  }
                                )}
                                pagination={false}
                                bordered
                                size="middle"
                                scroll={{ x: 1100 }}
                              />
                            </Form>

                            {/* Per-tool consumed-materials drawer */}
                            <Drawer
                              title={
                                materialsDrawerToolId != null
                                  ? `Consumed Materials — ${
                                      activityToolsList.find(
                                        (t) =>
                                          t.taskToolId === materialsDrawerToolId,
                                      )?.toolName || "Tool"
                                    }`
                                  : "Consumed Materials"
                              }
                              placement="right"
                              width={Math.min(
                                980,
                                typeof window !== "undefined"
                                  ? window.innerWidth - 80
                                  : 980,
                              )}
                              open={materialsDrawerToolId != null}
                              onClose={() => setMaterialsDrawerToolId(null)}
                              destroyOnClose
                              footer={
                                isEditMode ? (
                                  <div style={{ textAlign: "right" }}>
                                    <Button
                                      type="button"
                                      color="primary"
                                      onClick={() => {
                                        setMaterialsDrawerToolId(null);
                                        messageApi.open({
                                          type: "success",
                                          content: "Materials saved",
                                        });
                                      }}
                                    >
                                      <em className="icon ni ni-check-circle" />{" "}
                                      Save Materials
                                    </Button>
                                  </div>
                                ) : undefined
                              }
                            >
                              {materialsDrawerToolId != null && (
                                <TimeLogToolMaterialsEditor
                                  rows={getToolMaterialRows(
                                    materialsDrawerToolId,
                                  )}
                                  disabled={!isEditMode}
                                  materialNameMap={materialNameMap}
                                  onChange={(rows) =>
                                    setMaterialRowsByTool((prev) => ({
                                      ...prev,
                                      [materialsDrawerToolId as number]: rows,
                                    }))
                                  }
                                />
                              )}
                            </Drawer>
                            {activityToolsList.length > 0 && (
                              <SectionTotalRow
                                label="Tools Total"
                                amount={computeToolSectionTotalEdit()}
                              />
                            )}
                            {(activityResourcesList.length > 0 ||
                              activityToolsList.length > 0) && (
                              <SectionTotalRow
                                strong
                                label="Grand Total"
                                amount={
                                  computeResourceSectionTotalEdit() +
                                  computeToolSectionTotalEdit()
                                }
                              />
                            )}
                            {activityToolsList.length === 0 && (
                              <div
                                style={{
                                  textAlign: "center",
                                  padding: "20px",
                                  color: "#666",
                                }}
                              >
                                No tools available for this task. Please check
                                if the task has associated tools.
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="mt-3">
                            <div className="table-responsive poTable">
                              <Table
                                className="customTable"
                                rowKey={(record: any) => `${record.taskToolId}`}
                                columns={[
                                  {
                                    title: "Tool Type",
                                    dataIndex: "toolTypeName",
                                    key: "toolTypeName",
                                    width: 150,
                                    align: "center",
                                  },
                                  {
                                    title: "Tool/Machinery",
                                    dataIndex: "toolName",
                                    key: "toolName",
                                    width: 150,
                                    align: "center",
                                  },
                                  {
                                    title: "Serial Number",
                                    dataIndex: "serialNumber",
                                    key: "serialNumber",
                                    width: 130,
                                    align: "center",
                                    render: (text: any) =>
                                      text !== undefined &&
                                      text !== null &&
                                      text !== ""
                                        ? text
                                        : "-",
                                  },
                                  // Count column hidden for tools in timelogs
                                  // {
                                  //   title: "Count",
                                  //   dataIndex: "count",
                                  //   key: "count",
                                  //   width: 100,
                                  //   align: "center",
                                  // },
                                  {
                                    title: "Actual Quantity",
                                    dataIndex: "actualQuantity",
                                    key: "actualQuantity",
                                    width: 130,
                                    align: "center",
                                    render: (text: any) =>
                                      text !== undefined &&
                                      text !== null &&
                                      text !== ""
                                        ? text
                                        : "-",
                                  },
                                  {
                                    title: "Actual Rate",
                                    dataIndex: "actualRate",
                                    key: "actualRate",
                                    width: 130,
                                    align: "center",
                                    render: (text: any) =>
                                      text !== undefined &&
                                      text !== null &&
                                      text !== ""
                                        ? text
                                        : "-",
                                  },
                                  {
                                    title: "Total Amount",
                                    key: "toolTotalAmount",
                                    width: 140,
                                    align: "center",
                                    render: (_: any, record: any) => {
                                      const total = toolLineTotal(
                                        record.actualQuantity,
                                        record.actualRate
                                      );
                                      return total ? formatCurrency(total) : "-";
                                    },
                                  },
                                ]}
                                expandable={{
                                  rowExpandable: (r: any) =>
                                    (r.toolMaterials || []).length > 0,
                                  expandedRowRender: (r: any) => (
                                    <Table
                                      rowKey={(m: any) =>
                                        `${m.timeLogToolMaterialId}`
                                      }
                                      dataSource={r.toolMaterials || []}
                                      pagination={false}
                                      size="small"
                                      bordered
                                      title={() => "Consumed Materials"}
                                      columns={[
                                        {
                                          title: "Material",
                                          align: "center" as const,
                                          render: (_: any, m: any) =>
                                            materialNameMap[m.materialId] ||
                                            (m.materialId
                                              ? `Material #${m.materialId}`
                                              : "-"),
                                        },
                                        {
                                          title: "Planned Qty",
                                          align: "center" as const,
                                          render: (_: any, m: any) =>
                                            m.plannedQuantity != null &&
                                            `${m.plannedQuantity}` !== ""
                                              ? `${m.plannedQuantity}${
                                                  m.workUomName
                                                    ? ` ${m.workUomName}`
                                                    : ""
                                                }`
                                              : "-",
                                        },
                                        {
                                          title: "Planned Rate",
                                          align: "center" as const,
                                          render: (_: any, m: any) =>
                                            (m.plannedRate ?? "") !== ""
                                              ? m.plannedRate
                                              : "-",
                                        },
                                        {
                                          title: "Actual Qty",
                                          align: "center" as const,
                                          render: (_: any, m: any) =>
                                            m.actualQuantity != null &&
                                            `${m.actualQuantity}` !== ""
                                              ? `${m.actualQuantity}${
                                                  m.workUomName
                                                    ? ` ${m.workUomName}`
                                                    : ""
                                                }`
                                              : "-",
                                        },
                                        {
                                          title: "Actual Rate",
                                          align: "center" as const,
                                          render: (_: any, m: any) =>
                                            (m.actualRate ?? "") !== ""
                                              ? m.actualRate
                                              : "-",
                                        },
                                        {
                                          title: "Total Amount",
                                          align: "right" as const,
                                          render: (_: any, m: any) =>
                                            formatCurrency(
                                              m.totalAmount != null
                                                ? Number(m.totalAmount)
                                                : Number(m.actualQuantity || 0) *
                                                    Number(m.actualRate || 0),
                                            ),
                                        },
                                      ]}
                                    />
                                  ),
                                }}
                                dataSource={(() => {
                                  if (!timeLogData?.timeLogTools) return [];

                                  // Remove duplicates based on taskToolId
                                  const uniqueTools =
                                    timeLogData.timeLogTools.reduce(
                                      (acc: any[], current: any) => {
                                        const existingIndex = acc.findIndex(
                                          (item: any) =>
                                            item.taskToolId ===
                                            current.taskToolId
                                        );
                                        if (existingIndex === -1) {
                                          acc.push(current);
                                        }
                                        return acc;
                                      },
                                      []
                                    );

                                  return uniqueTools
                                    .map((timeLogTool: any) => {
                                      // Find the matching activity tool to get the full details
                                      const matchingTool =
                                        activityToolsList.find(
                                          (activityTool: any) =>
                                            activityTool.taskToolId ===
                                            timeLogTool.taskToolId
                                        );
                                      return matchingTool
                                        ? {
                                            ...matchingTool,
                                            count: timeLogTool.count || 0, // Show planned/allocated count
                                            actualQuantity:
                                              timeLogTool.actualQuantity,
                                            actualRate: timeLogTool.actualRate,
                                            // Logged actual materials (with
                                            // resolved planned details).
                                            toolMaterials:
                                              timeLogTool.toolMaterials || [],
                                          }
                                        : null;
                                    })
                                    .filter(Boolean);
                                })()}
                                scroll={{ x: "max-content" }}
                                bordered
                                size="middle"
                                pagination={false}
                                style={{ minWidth: "600px" }}
                              />
                            </div>
                            <SectionTotalRow
                              label="Tools Total"
                              amount={computeToolSectionTotalView()}
                            />
                            <SectionTotalRow
                              strong
                              label="Grand Total"
                              amount={
                                computeResourceSectionTotalView() +
                                computeToolSectionTotalView()
                              }
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Task Completion Percentage - Only show for tasks */}
                  {timeLogData?.taskId && (
                    <div className="col-12">
                      {isEditMode ? (
                        <InlineFieldEdit
                          label="Completion Percentage"
                          name="taskCompletionPercentage"
                          isEditMode={isEditMode}
                          isRequiredField={true}
                        >
                          <Controller
                            name="taskCompletionPercentage"
                            control={control}
                            rules={{
                              validate: (value) => {
                                const delta = Number(value ?? 0);
                                const absolute = completionFloor + delta;
                                if (absolute <= completionFloor) {
                                  return `Completion must be greater than ${completionFloor}%`;
                                }
                                if (absolute >= 100) {
                                  return "Completion must be less than 100%";
                                }
                                return true;
                              },
                            }}
                            render={({ field, fieldState }) => {
                              const deltaValue =
                                typeof field.value === "number"
                                  ? field.value
                                  : field.value !== null &&
                                    field.value !== undefined &&
                                    field.value !== ""
                                  ? Number(field.value)
                                  : 0;
                              const absoluteValue = Math.min(
                                100,
                                Math.max(
                                  completionFloor,
                                  completionFloor + deltaValue
                                )
                              );
                              const sliderMarks: Record<number, string> = {
                                0: "0%",
                                25: "25%",
                                50: "50%",
                                75: "75%",
                                100: "100%",
                              };
                              if (
                                completionFloor > 0 &&
                                !sliderMarks[completionFloor]
                              ) {
                                sliderMarks[
                                  completionFloor
                                ] = `${completionFloor}%`;
                              }
                              const newDelta = Math.max(
                                0,
                                absoluteValue - completionFloor
                              );
                              return (
                                <div style={{ width: "100%" }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      marginBottom: 4,
                                    }}
                                  >
                                    {completionFloor > 0 ? (
                                      <span
                                        style={{
                                          fontSize: 12,
                                          color: "#666",
                                        }}
                                      >
                                        Pre-log: {completionFloor}% · This log
                                        adds: {newDelta}%
                                      </span>
                                    ) : (
                                      <span />
                                    )}
                                    <span
                                      style={{
                                        fontWeight: 600,
                                        color: "#1677ff",
                                        fontSize: 14,
                                      }}
                                    >
                                      {absoluteValue}%
                                    </span>
                                  </div>
                                  <Slider
                                    min={0}
                                    max={100}
                                    step={1}
                                    value={absoluteValue}
                                    onChange={(v) => {
                                      const next = Math.max(
                                        completionFloor,
                                        v as number
                                      );
                                      const nextDelta = Math.max(
                                        0,
                                        next - completionFloor
                                      );
                                      setValue(
                                        "taskCompletionPercentage",
                                        nextDelta as any,
                                        {
                                          shouldValidate: true,
                                          shouldDirty: true,
                                          shouldTouch: true,
                                        }
                                      );
                                      methods.clearErrors(
                                        "taskCompletionPercentage"
                                      );
                                    }}
                                    marks={sliderMarks}
                                    tooltip={{
                                      formatter: (v) => `${v}%`,
                                    }}
                                  />
                                  {fieldState.error && (
                                    <div
                                      className="text-danger"
                                      style={{
                                        fontSize: 12,
                                        marginTop: 4,
                                      }}
                                    >
                                      {fieldState.error.message}
                                    </div>
                                  )}
                                </div>
                              );
                            }}
                          />
                        </InlineFieldEdit>
                      ) : (
                        <FieldDisplay
                          heading="Completion Percentage"
                          value={
                            timeLogData?.taskCompletionPercentage !== null &&
                            timeLogData?.taskCompletionPercentage !== undefined
                              ? `${timeLogData.taskCompletionPercentage}%`
                              : "—"
                          }
                        />
                      )}
                    </div>
                  )}
                  {/* Notes */}
                  <div className="col-12">
                    {isEditMode ? (
                      <InlineFieldEdit
                        label="Notes"
                        name="notes"
                        isEditMode={isEditMode}
                      >
                        <FormInput
                          name="notes"
                          label=""
                          type="textarea"
                          rows={4}
                          placeholder="Enter notes"
                        />
                      </InlineFieldEdit>
                    ) : (
                      <FieldDisplay
                        heading="Notes"
                        // value={watch("notes") || "—"}
                        value={
                          watch("notes") ? (
                            <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                              {watch("notes")}
                            </ReactMarkdown>
                          ) : (
                            "—"
                          )
                        }
                      />
                    )}
                  </div>

                  {/* Files section */}
                  <div className="col-12 mt-3">
                    <h6
                      className="fw-semibold mb-3"
                      style={{
                        color: "#333",
                        borderBottom: "2px solid #f0f0f0",
                        paddingBottom: "8px",
                      }}
                    >
                      Uploaded Files
                    </h6>

                    {isEditMode ? (
                      // Edit mode: show Upload.Dragger for new files and list with remove + remarks inputs
                      <>
                        <div
                          style={{
                            border: "1px dashed #d9d9d9",
                            borderRadius: "6px",
                            padding: "16px",
                            backgroundColor: "#fafafa",
                            marginBottom: "16px",
                          }}
                        >
                          <Upload.Dragger
                            multiple
                            showUploadList={false}
                            customRequest={({ file, onSuccess }) => {
                              setTimeout(() => {
                                onSuccess?.("ok");
                                handleFileUpload({
                                  file: { originFileObj: file, status: "done" },
                                });
                              }, 300);
                            }}
                            onRemove={handleRemoveFile}
                            style={{
                              background: "transparent",
                              padding: "4px 0",
                            }}
                          >
                            <div
                              style={{
                                textAlign: "center",
                                fontSize: 14,
                                color: "#555",
                                padding: "20px 0",
                              }}
                            >
                              <UploadOutlined
                                style={{
                                  marginRight: 8,
                                  fontSize: "20px",
                                  color: "#1890ff",
                                }}
                              />
                              <div>Click or drag files to upload</div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#999",
                                  marginTop: "4px",
                                }}
                              >
                                Support for multiple files upload
                              </div>
                            </div>
                          </Upload.Dragger>
                        </div>

                        {uploadedFiles.length > 0 && (
                          <div style={{ marginTop: "16px" }}>
                            <div
                              className="mb-2 fw-medium"
                              style={{ fontSize: "14px" }}
                            >
                              File List ({uploadedFiles.length})
                            </div>

                            {uploadedFiles.map((file, index) => {
                              const isExistingFile = !!file.docId;
                              const fileExtension = isExistingFile
                                ? file.fileName?.split(".").pop()?.toUpperCase()
                                : file.name?.split(".").pop()?.toUpperCase();

                              const getFileIcon = (ext: string | undefined) => {
                                if (!ext) return "📄";
                                const imgExts = [
                                  "JPG",
                                  "JPEG",
                                  "PNG",
                                  "GIF",
                                  "BMP",
                                  "SVG",
                                ];
                                const docExts = ["DOC", "DOCX"];
                                const pdfExts = ["PDF"];
                                const sheetExts = ["XLS", "XLSX", "CSV"];

                                if (imgExts.includes(ext)) return "🖼️";
                                if (docExts.includes(ext)) return "📝";
                                if (pdfExts.includes(ext)) return "📄";
                                if (sheetExts.includes(ext)) return "📊";
                                return "📄";
                              };

                              return (
                                <div
                                  key={file.docId ?? `new-${index}`}
                                  style={{
                                    fontSize: 14,
                                    padding: "12px",
                                    border: "1px solid #f0f0f0",
                                    borderRadius: "6px",
                                    marginBottom: "8px",
                                    backgroundColor: "#fff",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 8,
                                      marginBottom: "8px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                      }}
                                    >
                                      <span style={{ fontSize: "16px" }}>
                                        {getFileIcon(fileExtension)}
                                      </span>
                                      <span>
                                        {isExistingFile ? (
                                          <a
                                            href={file.filePath}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                              fontWeight: "500",
                                              color: "#2380f7",
                                              textDecoration: "none",
                                            }}
                                          >
                                            {file.fileName}
                                          </a>
                                        ) : (
                                          <span style={{ fontWeight: "500" }}>
                                            {file.name}
                                          </span>
                                        )}
                                      </span>
                                      {fileExtension && (
                                        <Tag
                                          color="blue"
                                          style={{
                                            fontSize: "10px",
                                            padding: "0 4px",
                                          }}
                                        >
                                          {fileExtension}
                                        </Tag>
                                      )}
                                    </div>
                                    <Button
                                      className="btn-icon"
                                      size="sm"
                                      onClick={() => handleRemoveFile(file)}
                                      style={{
                                        background: "transparent",
                                        border: "none",
                                        boxShadow: "none",
                                        padding: 0,
                                        fontSize: 14,
                                        color: "#ff4d4f",
                                      }}
                                    >
                                      <Icon name="trash" />
                                    </Button>
                                  </div>
                                  <Input
                                    ref={(el) =>
                                      (remarksRefs.current[index] = el)
                                    }
                                    placeholder="Enter remarks for this file"
                                    value={fileRemarks[index] || ""}
                                    onChange={(e) =>
                                      handleRemarkChange(index, e.target.value)
                                    }
                                    style={{
                                      fontSize: "13px",
                                    }}
                                    onFocus={(e) => e.target.select()}
                                    disabled={false}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    ) : // View mode: show files in a well-formatted list
                    files.length === 0 ? (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          color: "#999",
                          border: "1px dashed #d9d9d9",
                          borderRadius: "6px",
                        }}
                      >
                        <Icon
                          name="file"
                          style={{ fontSize: "24px", marginBottom: "8px" }}
                        />
                        <div>No files uploaded</div>
                      </div>
                    ) : (
                      <div>
                        <div
                          className="mb-2 fw-medium"
                          style={{ fontSize: "14px" }}
                        >
                          Uploaded Files ({files.length})
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          {files.map((file: any) => {
                            const fileExtension = file.fileName
                              ?.split(".")
                              .pop()
                              ?.toUpperCase();

                            const getFileIcon = (ext: string | undefined) => {
                              if (!ext) return "📄";
                              const imgExts = [
                                "JPG",
                                "JPEG",
                                "PNG",
                                "GIF",
                                "BMP",
                                "SVG",
                              ];
                              const docExts = ["DOC", "DOCX"];
                              const pdfExts = ["PDF"];
                              const sheetExts = ["XLS", "XLSX", "CSV"];

                              if (imgExts.includes(ext)) return "🖼️";
                              if (docExts.includes(ext)) return "📝";
                              if (pdfExts.includes(ext)) return "📄";
                              if (sheetExts.includes(ext)) return "📊";
                              return "📄";
                            };

                            return (
                              <div
                                key={file.docId}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  padding: "12px",
                                  border: "1px solid #f0f0f0",
                                  borderRadius: "6px",
                                  backgroundColor: "#fff",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px",
                                  }}
                                >
                                  <span style={{ fontSize: "18px" }}>
                                    {getFileIcon(fileExtension)}
                                  </span>
                                  <div>
                                    <a
                                      href={file.filePath}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        fontWeight: "500",
                                        color: "#2380f7",
                                        textDecoration: "none",
                                        display: "block",
                                      }}
                                    >
                                      {file.fileName}
                                    </a>
                                    {file.remarks && (
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          color: "#666",
                                          marginTop: "4px",
                                        }}
                                      >
                                        {file.remarks}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {fileExtension && (
                                  <Tag
                                    color="blue"
                                    style={{
                                      fontSize: "10px",
                                      padding: "2px 6px",
                                    }}
                                  >
                                    {fileExtension}
                                  </Tag>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </form>
            </FormProvider>

            {/* <div className="d-flex align-items-center flex-wrap mb-2 mt-2">
            <ul className="nav nav-tabs nav-tabs-card nav-tabs-xs w-100 p-0 m-0 position-relative ">
              {TabsList?.map((_tab: any, index: number) => {
                return (
                  <li
                    className="nav-item"
                    key={_tab?.tabKey}
                    onClick={() => setActiveTab(() => _tab?.tabKey)}
                  >
                    <a
                      onClick={(ev) => {
                        ev.preventDefault();
                      }}
                      className={`nav-link${
                        activeTab === _tab?.tabKey ? " active" : ""
                      }`}
                      href={"#" + _tab?.tabKey}
                    >
                      {_tab?.label}
                    </a>
                  </li>
                );
              })}
            </ul>

            <div className="tab-content mt-2 w-100">
              <div
                key={"stream"}
                className={`tab-pane${
                  activeTab === "stream" ? " active" : " "
                }`}
              >
                <Card style={{ margin: "16px 0", border: "1px solid #f0f0f0" }}>
                  {loading ? (
                    <Flex align="center" gap="middle" className="pageLoader">
                      <Spin
                        indicator={<LoadingOutlined spin />}
                        className="m-auto"
                        size="large"
                      />
                    </Flex>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "260px",
                        // border: "1px dashed #d9d9d9",
                        borderRadius: 8,
                        // background: "#fafafa",
                      }}
                    >
                      {isEmpty ? (
                        <Empty description="No Data Found" />
                      ) : (
                        <div>No Data found</div>
                      )}
                    </div>
                  )}
                </Card>
              </div>
            </div>
          </div> */}
          </div>
        </Card>
      </>
    );
  }
);

export default TimeLogInformationById;
